import React, { useState, useEffect, useMemo, useRef, Suspense } from "react";
import ReactDOM from "react-dom";
import * as XLSX from "xlsx";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faPlus,
  faTrash,
  faSave,
  faFileCsv,
  faRotate,
  faLocationDot,
  faMicrochip,
  faTriangleExclamation,
  faSatelliteDish,
  faUpload,
  faTrophy,
  faCertificate,
} from "@fortawesome/free-solid-svg-icons";
import moment from "moment-timezone";
import { useAuth, Button, InputField } from "@vionixasia/shared-components";
import { useNotification } from "@vionixasia/shared-components/common/NotificationContext";
import { getFirestore, getDoc, getDocs, collection, doc, setDoc } from "firebase/firestore";
import { getStorage, ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { faX } from "@fortawesome/free-solid-svg-icons";
import { generateAndDownloadExcel } from "../utils/exportUtils";
import { generateAndDownloadPodiumPdf } from "../utils/podiumExport";
import { generateAndDownloadItraXlsx } from "../utils/itraExport";
import { toDate } from "../utils/firestoreTime";
import { CertificateDesigner } from "../components/CertificateDesigner";

// One shared style for the Quick Links cells. They fill their grid cell — the
// grid decides the width — and stay visually quieter than a primary button,
// because these are navigation, not actions on the form beside them.
const QUICK_CHIP =
  "flex w-full items-center justify-center gap-1.5 rounded-lg border " +
  "border-slate-200 bg-white px-3 py-2 text-xs font-medium text-slate-700 " +
  "no-underline text-center transition-colors hover:border-slate-300 " +
  "hover:bg-slate-50 focus:outline-none focus-visible:ring-2 " +
  "focus-visible:ring-slate-400";

// Places printed per gender on the podium recap. Ten is what a prize desk
// works from: the top three take the podium, the rest cover no-shows.
const PODIUM_PLACES = 10;

// Layout utility
const Section = ({ title, children, className = "" }) => (
  <section
    className={`bg-white rounded-card border border-slate-200 p-4 mb-4 ${className}`}
  >
    {title && (
      <h2 className="text-lg font-bold text-slate-900 mb-4 font-oxanium">
        {title}
      </h2>
    )}
    {children}
  </section>
);

const FormField = ({ className = "", wrapperClassName = "", ...props }) => (
  <InputField
    labelColor="text-slate-700"
    className={className}
    wrapperClassName={wrapperClassName}
    {...props}
  />
);

// Loading skeleton
const LoadingSkeleton = () => (
  <div className="space-y-8">
    <div className="bg-white rounded-xl p-6 shadow-sm">
      <div className="h-8 w-1/4 bg-slate-300 rounded animate-pulse mb-4"></div>
      <div className="space-y-3">
        <div className="h-4 w-3/4 bg-slate-300 rounded animate-pulse"></div>
        <div className="h-4 w-1/2 bg-slate-300 rounded animate-pulse"></div>
      </div>
    </div>
    <div className="bg-white rounded-xl p-6 shadow-sm">
      <div className="h-8 w-1/3 bg-slate-300 rounded animate-pulse mb-4"></div>
      <div className="space-y-3">
        <div className="h-4 w-2/3 bg-slate-300 rounded animate-pulse"></div>
        <div className="h-4 w-3/4 bg-slate-300 rounded animate-pulse"></div>
      </div>
    </div>
  </div>
);

/**
 * Format a stored schedule value for a `datetime-local` input.
 *
 * Accepts every shape a category time has been stored as: a Firestore Timestamp
 * (what the schedule uses now), an ISO string (older documents), or a Date.
 * A Timestamp must be unwrapped before moment() sees it — moment cannot parse
 * one and returns "Invalid date", which the input silently renders as 00:00.
 * Returns "" when the value is missing or unparseable, so a blank field is
 * visibly empty rather than a plausible-looking midnight.
 */
/** Every event_timing document, as GET /event_timing returned them. */
const loadEvents = async () => {
  const snapshot = await getDocs(collection(getFirestore(), "event_timing"));
  return snapshot.docs.map((d) => ({ ...d.data(), eventId: d.data().eventId || d.id }));
};

const toDateTimeLocal = (value, timeZone) => {
  const raw = toDate(value);
  if (!raw) return "";
  const parsed = moment(raw).tz(timeZone);
  return parsed.isValid() ? parsed.format("YYYY-MM-DDTHH:mm") : "";
};

/**
 * The label a band would get from its own range: "16-24", or "50+" when the
 * upper bound is open. Only fills a blank or still-derived label, so a name the
 * operator typed by hand ("Youth", "Veteran") survives editing the numbers.
 */
const _autoLabel = (min, max, current) => {
  const derived =
    min === "" || min === null || min === undefined
      ? ""
      : max === null || max === undefined || max === ""
        ? `${min}+`
        : `${min}-${max}`;
  const trimmed = String(current || "").trim();
  const looksDerived = /^\d+(-\d+|\+)?$/.test(trimmed);
  return !trimmed || looksDerived ? derived : current;
};

export const Admintiming = () => {
  const { user, getIdToken } = useAuth();
  const { showNotification } = useNotification();
  const [isAdmin, setIsAdmin] = useState(false);
  const db = getFirestore();
  const navigate = useNavigate();
  const userTimeZone =
    Intl.DateTimeFormat().resolvedOptions().timeZone || "Asia/Makassar";

  const [eventName, setEventName] = useState("");
  const [eventDate, setEventDate] = useState("");
  const [eventSlug, setEventSlug] = useState(""); // New State for Clean URL
  const [eventStatus, setEventStatus] = useState("ongoing");
  const [enableCertificate, setEnableCertificate] = useState(false);
  // Age-group results were reachable only by hard-coding an event id in the
  // router, so every new event that grades by age needed a code change. This
  // makes it a property of the event instead.
  const [enableAgeGroup, setEnableAgeGroup] = useState(false);
  const [showSplits, setShowSplits] = useState(false);
  // Bands the event grades by. Empty means "use the built-in masters set", so an
  // event that never configured them keeps ranking exactly as it always did.
  const [ageGroups, setAgeGroups] = useState([]);
  const [showAgeGroupEditor, setShowAgeGroupEditor] = useState(false);
  // Per-event certificate artwork + text positions (see utils/certificateLayout).
  const [certificateBackground, setCertificateBackground] = useState("");
  const [certificateBgFile, setCertificateBgFile] = useState(null);
  const [certificateLayout, setCertificateLayout] = useState(null);

  /**
   * Preview URL for the designer.
   *
   * Memoised on the File itself, not derived inline: calling createObjectURL
   * during render minted a fresh blob URL on every keystroke and every drag
   * frame, which leaked the previous ones and forced the designer to re-decode
   * the image each time — the flicker operators saw while dragging a block.
   */
  const certificateBgPreview = useMemo(
    () => (certificateBgFile ? URL.createObjectURL(certificateBgFile) : certificateBackground),
    [certificateBgFile, certificateBackground],
  );

  useEffect(() => {
    if (!certificateBgFile) return;
    return () => URL.revokeObjectURL(certificateBgPreview);
  }, [certificateBgFile, certificateBgPreview]);

  /**
   * A File stages an upload; null clears the artwork outright — including a
   * background already saved on the event, which is the only way to get back to
   * a plain certificate once a client's design has been uploaded.
   */
  const handleCertificateBgChange = (file) => {
    setCertificateBgFile(file);
    if (!file) setCertificateBackground("");
  };

  const [showCertDesigner, setShowCertDesigner] = useState(false);
  const [categories, setCategories] = useState([
    {
      id: "",
      name: "",
      startTime: "",
      cutOffTime: "",
      checkpoints: [{ id: "cp1", name: "", locationId: "", type: "cp" }],
    },
  ]);
  const [eventImageFile, setEventImageFile] = useState(null);
  const [eventImageUrl, setEventImageUrl] = useState("");
  const [location, setLocation] = useState("");
  const [livetrackUrl, setLivetrackUrl] = useState("");
  const [file, setFile] = useState(null);
  const [message, setMessage] = useState("");
  const [eventSaved, setEventSaved] = useState(false);
  const [events, setEvents] = useState([]);
  const [selectedEventId, setSelectedEventId] = useState("");
  const isSaving = useRef(false);

  /**
   * Local draft of the certificate layout.
   *
   * Placing text over client artwork is fiddly work that can take many minutes,
   * and none of it used to survive closing the tab — the designer's numbers
   * lived only in component state until the whole event was saved. This keeps a
   * per-event copy in localStorage so the work is recoverable.
   *
   * Deliberately local-only: writing half-finished layouts to Firestore would
   * change how a live event renders certificates mid-edit. The draft is a
   * safety net for the operator, not a publishing step — saving the event is
   * still what makes a layout real.
   *
   * The artwork file is NOT part of the draft: a File cannot be serialised, and
   * an uploaded image already persists on the event once saved.
   *
   * Declared here, below `events` and `selectedEventId`, because it reads both —
   * placing it up with the other certificate state put `selectedEventId` in its
   * own temporal dead zone and blanked the whole admin page.
   */
  const certDraftKey = selectedEventId ? `certDraft:${selectedEventId}` : null;
  const [certDraftAt, setCertDraftAt] = useState(null);

  // Guards the first render after an event loads, so restoring server state
  // never immediately overwrites a draft that is still on disk.
  const certDraftHydrated = useRef(null);

  useEffect(() => {
    if (!certDraftKey) return;
    // Re-arm on every event switch; the effect below only writes once armed.
    certDraftHydrated.current = null;
    try {
      const raw = localStorage.getItem(certDraftKey);
      if (!raw) {
        setCertDraftAt(null);
        return;
      }
      const parsed = JSON.parse(raw);
      if (parsed?.layout) {
        setCertificateLayout(parsed.layout);
        setCertDraftAt(parsed.savedAt || null);
      }
    } catch {
      // A corrupt or unreadable draft must never block the editor.
      setCertDraftAt(null);
    }
  }, [certDraftKey, events]);

  useEffect(() => {
    if (!certDraftKey) return;
    // Skip the render that immediately follows hydration/event-load.
    if (certDraftHydrated.current !== certDraftKey) {
      certDraftHydrated.current = certDraftKey;
      return;
    }
    if (!certificateLayout) return;
    try {
      const savedAt = new Date().toISOString();
      localStorage.setItem(certDraftKey, JSON.stringify({ layout: certificateLayout, savedAt }));
      setCertDraftAt(savedAt);
    } catch {
      // Quota or private-mode failures are not worth interrupting the edit for.
    }
  }, [certificateLayout, certDraftKey]);

  const clearCertDraft = () => {
    if (!certDraftKey) return;
    try {
      localStorage.removeItem(certDraftKey);
    } catch {
      /* nothing useful to do */
    }
    setCertDraftAt(null);
  };

  const authenticatedFetch = async (url, options = {}) => {
    const token = await getIdToken();
    return fetch(url, {
      ...options,
      headers: { ...(options.headers || {}), Authorization: `Bearer ${token}` },
    });
  };

  // === CSV UPLOAD STATE ===
  // `uploadContext` pinpoints where the current progress message should render
  // so the banner never leaks outside the card that triggered the action.
  //   { scope: 'participant', categoryId } → shown inside that category card
  //   { scope: 'results' }                 → shown inside the Import Results card
  //   null                                 → no upload in flight
  const [uploadContext, setUploadContext] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadMessage, setUploadMessage] = useState("");
  const [uploadTone, setUploadTone] = useState("info"); // info | success | error
  const [isUploading, setIsUploading] = useState(false);

  // === MYLAPS TCP MONITOR ===
  // Real-time status of MYLAPS T&S plugins currently connected to the
  // `mylaps-ingest` server. Fields are stored per-client after AckGetInfo /
  // GetLocations responses arrive. Populated through the endpoints in
  // @/Users/efantrifebriansyah/Developer/VionixAsia/mylaps-ingest/index.js:854-918.
  const MYLAPS_INGEST_BASE = "https://live-timing.fly.dev";
  const [tcpClients, setTcpClients] = useState([]);
  const [tcpLastAction, setTcpLastAction] = useState("");
  const [tcpLoading, setTcpLoading] = useState(false);
  const [tcpChecked, setTcpChecked] = useState(false);
  const [tcpFetchError, setTcpFetchError] = useState("");
  const [tcpExpanded, setTcpExpanded] = useState({}); // clientKey → bool
  const [raceReadiness, setRaceReadiness] = useState(null);
  const [readinessError, setReadinessError] = useState("");
  // Which event the MYLAPS ingest is actually filing passings into. The decoder
  // never names an event: the ingest picks one from the `ongoing` events in
  // Firestore, so a stale DEFAULT_EVENT_ID or a second ongoing event silently
  // routes an entire race into the wrong document. Surfaced here because that
  // failure is invisible from the admin form itself.
  const [ingestHealth, setIngestHealth] = useState(null);
  const [ingestError, setIngestError] = useState("");
  const [ingestRefreshing, setIngestRefreshing] = useState(false);
  // Race-day vs standby capacity for the Fly apps. These only ever changed by
  // hand from a laptop, guided by a comment inside each fly.toml — which is how
  // an entire event once ran on shared CPU, and how the timing API sat at
  // scale-to-zero under a comment claiming it was kept warm.
  const [flyOps, setFlyOps] = useState(null);
  const [flyOpsError, setFlyOpsError] = useState("");
  const [flyOpsBusy, setFlyOpsBusy] = useState("");
  // One-time setup instruction for the whole panel (missing Fly token), kept
  // separate from per-app errors so it is stated once rather than per row.
  const [flySetup, setFlySetup] = useState("");
  // Pending standby that needs an explicit yes because an event is still live.
  const [flyConfirm, setFlyConfirm] = useState(null);
  const [relayLocks, setRelayLocks] = useState({});
  const [relayLockDrafts, setRelayLockDrafts] = useState({});
  const [relayLockBusy, setRelayLockBusy] = useState("");
  const [relaySwitchConfirm, setRelaySwitchConfirm] = useState(null);

  // "shared/1cpu/512mb" or "performance/2cpu/4gb". Sub-GB boxes must stay in mb:
  // rounding 256mb to GB rendered it as "0gb".
  const formatGuest = (guest) => {
    if (!guest) return "";
    const mb = Number(guest.memory_mb);
    const mem = mb >= 1024 ? `${mb / 1024}gb` : `${mb}mb`;
    return `${guest.cpu_kind}/${guest.cpus}cpu/${mem}`;
  };

  const fetchFlyOps = async () => {
    try {
      setFlyOpsError("");
      const res = await authenticatedFetch(
        `${import.meta.env.VITE_API_BASE_URL}/api/flyops/status`,
      );
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || `HTTP ${res.status}`);
      setFlyOps(data.apps || []);
      setFlySetup(data.setupRequired || "");
    } catch (error) {
      setFlyOpsError(error.message);
      setFlyOps(null);
    }
  };

  // The response carries `verified`, read back from Fly after the write, because
  // Fly has accepted a config and silently ignored it before.
  const setFlyMode = async (appName, mode, confirm = false) => {
    setFlyOpsBusy(appName);
    try {
      setFlyOpsError("");
      const res = await authenticatedFetch(
        `${import.meta.env.VITE_API_BASE_URL}/api/flyops/mode`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ app: appName, mode, confirm }),
        },
      );
      const data = await res.json();
      // Standby during a live event is allowed, but not by accident.
      if (res.status === 409 && data.needsConfirm) {
        setFlyConfirm({ app: appName, mode, message: data.message });
        return;
      }
      if (!res.ok) throw new Error(data.message || `HTTP ${res.status}`);
      showNotification(
        data.verified ? "success" : "warning",
        data.verified
          ? `${appName} is now in ${mode} mode.${data.note ? " " + data.note : ""}`
          : `${appName} was updated but did not read back as ${mode} — check Fly.`,
      );
      await fetchFlyOps();
    } catch (error) {
      setFlyOpsError(error.message);
      showNotification("error", `Fly update failed: ${error.message}`);
    } finally {
      setFlyOpsBusy("");
    }
  };

  // /health is the ingest's own view of the world: which event it will file
  // passings into, and how many decoders are connected. It is the only place
  // that truth exists — the admin form cannot infer it.
  const fetchIngestHealth = async () => {
    try {
      setIngestError("");
      const res = await fetch(`${MYLAPS_INGEST_BASE}/health`);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      setIngestHealth(await res.json());
    } catch (error) {
      setIngestError(error.message);
      setIngestHealth(null);
    }
  };

  // The ingest re-discovers its active event on a 5-minute timer, so a status
  // change made in admin does not take effect immediately. This forces it.
  const refreshIngestEvent = async () => {
    setIngestRefreshing(true);
    try {
      setIngestError("");
      const res = await authenticatedFetch(`${MYLAPS_INGEST_BASE}/refresh`, {
        method: "POST",
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || `HTTP ${res.status}`);
      showNotification(
        "success",
        `Ingest re-checked: now targeting ${data.after || "no event"}.`,
      );
      await fetchIngestHealth();
    } catch (error) {
      setIngestError(error.message);
      showNotification("error", `Ingest refresh failed: ${error.message}`);
    } finally {
      setIngestRefreshing(false);
    }
  };

  const fetchRaceReadiness = async () => {
    if (!selectedEventId) return;
    try {
      setReadinessError("");
      const res = await fetch(`${import.meta.env.VITE_API_BASE_URL}/api/timing/readiness?eventId=${encodeURIComponent(selectedEventId)}`);
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || `HTTP ${res.status}`);
      setRaceReadiness(data);
    } catch (error) {
      setReadinessError(error.message);
      setRaceReadiness(null);
    }
  };

  const tcpFetchClients = async () => {
    setTcpLoading(true);
    setTcpFetchError("");
    try {
      const res = await authenticatedFetch(`${MYLAPS_INGEST_BASE}/tcp/clients`);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setTcpClients(
        (data.clients || []).filter(
          (client) => client.handshakeComplete === true,
        ),
      );
    } catch (err) {
      setTcpFetchError(err.message);
    } finally {
      setTcpChecked(true);
      setTcpLoading(false);
    }
    await fetchRaceReadiness();
    await fetchIngestHealth();
  };

  useEffect(() => {
    fetchIngestHealth();
    fetchFlyOps();
    if (selectedEventId) fetchRaceReadiness();
    else setRaceReadiness(null);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedEventId]);

  const fetchRelayLocks = async (categoryId) => {
    if (!selectedEventId || !categoryId) return;
    setRelayLocks((current) => ({ ...current, [categoryId]: { ...(current[categoryId] || {}), loading: true, error: "" } }));
    try {
      const res = await fetch(`${import.meta.env.VITE_API_BASE_URL}/api/timing/relay-active-runners?eventId=${encodeURIComponent(selectedEventId)}&categoryId=${encodeURIComponent(categoryId)}`);
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || `HTTP ${res.status}`);
      setRelayLocks((current) => ({ ...current, [categoryId]: { loading: false, error: "", teams: data.teams || [] } }));
      setRelayLockDrafts((current) => ({
        ...current,
        [categoryId]: Object.fromEntries((data.teams || []).map((team) => [team.teamId, current[categoryId]?.[team.teamId] || team.activeRunnerBib])),
      }));
    } catch (error) {
      setRelayLocks((current) => ({ ...current, [categoryId]: { loading: false, error: error.message, teams: [] } }));
    }
  };

  const requestRelayRunnerActivation = (categoryId, teamId) => {
    const bib = relayLockDrafts[categoryId]?.[teamId];
    if (!bib) return;
    const team = (relayLocks[categoryId]?.teams || []).find((item) => item.teamId === teamId);
    const nextRunner = team?.members?.find((member) => member.bib === bib);
    if (!team || !nextRunner || bib === team.activeRunnerBib) return;
    setRelaySwitchConfirm({
      categoryId,
      teamId,
      teamName: team.teamName || teamId,
      currentBib: team.activeRunnerBib || "-",
      currentName: team.activeRunnerName || "",
      nextBib: bib,
      nextName: nextRunner.name || "",
    });
  };

  const activateRelayRunner = async ({ categoryId, teamId, nextBib: bib }) => {
    const busyKey = `${categoryId}:${teamId}`;
    setRelayLockBusy(busyKey);
    try {
      const res = await authenticatedFetch(`${import.meta.env.VITE_API_BASE_URL}/api/timing/relay-active-runner`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ eventId: selectedEventId, categoryId, teamId, bib }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || `HTTP ${res.status}`);
      showNotification("success", `${data.activeRunnerName} (BIB ${data.activeRunnerBib}) is now active.`);
      await fetchRelayLocks(categoryId);
    } catch (error) {
      showNotification("error", error.message);
    } finally {
      setRelayLockBusy("");
    }
  };

  const relayLockCategoryKey = categories
    .filter((category) => category.isRelay && category.relayTimingMode === "member_chips")
    .map((category) => category.id)
    .filter(Boolean)
    .join("|");

  useEffect(() => {
    if (!selectedEventId) {
      setRelayLocks({});
      setRelayLockDrafts({});
      return;
    }
    relayLockCategoryKey.split("|").filter(Boolean).forEach((categoryId) => fetchRelayLocks(categoryId));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedEventId, relayLockCategoryKey]);

  const tcpBroadcast = async (path, label) => {
    setTcpLoading(true);
    try {
      const res = await authenticatedFetch(`${MYLAPS_INGEST_BASE}${path}`, {
        method: "POST",
      });
      const data = await res.json();
      setTcpLastAction(
        `${label} → sent to ${data.sentTo?.length || 0} client(s)`,
      );
      // Auto-refresh after 2s to pick up async responses
      setTimeout(tcpFetchClients, 2000);
    } catch (err) {
      setTcpLastAction(`Error: ${err.message}`);
    } finally {
      setTcpLoading(false);
    }
  };

  // Check admin status
  useEffect(() => {
    if (!user) {
      showNotification("error", "Please sign in as an admin first.");
      navigate("/Login");
      return;
    }

    const checkAdmin = async () => {
      try {
        const userDoc = await getDoc(doc(db, "users", user.uid));
        if (userDoc.exists() && userDoc.data().role === "admin") {
          setIsAdmin(true);
        } else {
          showNotification(
            "error",
            "Access denied: you do not have admin permissions.",
          );
          navigate("/");
        }
      } catch (err) {
        console.error("Error checking admin:", err);
        showNotification(
          "error",
          "Failed to verify admin status. Please try again.",
        );
        navigate("/Login");
      }
    };

    checkAdmin();
  }, [user, db, navigate, showNotification]);

  // Fetch existing events
  //
  // Read straight from Firestore (event_timing is a public read), not through
  // GET /event_timing: in standby the backend sleeps, and the event dropdown
  // sat empty for the ~10s cold start every time the panel was opened.
  useEffect(() => {
    if (!isAdmin) return;

    const fetchEvents = async () => {
      try {
        const data = await loadEvents();
        setEvents(data);
        if (data.length === 0) {
          setMessage("No events found in the database.");
        }
      } catch (error) {
        setMessage("Error fetching events: " + error.message);
      }
    };
    fetchEvents();
  }, [isAdmin]);

  // Load event data
  useEffect(() => {
    if (!isAdmin || !selectedEventId || isSaving.current) return;

    const event = events.find((e) => e.eventId === selectedEventId);
    if (event) {
      setEventName(event.eventName);
      setEventDate(event.date);
      setEventStatus(event.status || "ongoing");
      setEnableCertificate(event.enableCertificate || false);
      setEnableAgeGroup(event.enableAgeGroup || false);
      setShowSplits(event.showSplits === true);
      setAgeGroups(Array.isArray(event.ageGroups) ? event.ageGroups : []);
      setCertificateBackground(event.certificateBackground || "");
      setCertificateBgFile(null);
      setCertificateLayout(event.certificateLayout || null);
      setEventImageUrl(event.heroImage || "");
      setLocation(event.location || "");
      setLivetrackUrl(
        event.livetrackSlug
          ? event.livetrackSlug.startsWith("http")
            ? event.livetrackSlug
            : `https://tracking.vionixasia.com/map/${event.livetrackSlug}`
          : "",
      );
      setEventSlug(event.slug || "");
      setCategories(
        (event.categories || []).map((cat) => ({
          id: cat.id,
          name: cat.name,
          // Firestore returns a Timestamp object, and moment() cannot parse one —
          // it yields "Invalid date", the datetime-local input rejects that and
          // falls back to 00:00, and the next Save writes that midnight over the
          // real schedule. Unwrap with toDate() first; ISO strings still work for
          // documents written before the schedule was stored as Timestamps.
          startTime: toDateTimeLocal(cat.startTime, userTimeZone),
          cutOffTime: toDateTimeLocal(cat.cutOffTime, userTimeZone),
          checkpoints: cat.checkpoints.map((cp) => ({
            id: cp.id,
            name: cp.name,
            locationId:
              cp.locationId || cp.name.toLowerCase().replace(/\s+/g, "_"),
            // Legacy checkpoints have no `type`; default to 'cp' (split/intermediate).
            type: cp.type || "cp",
          })) || [{ id: "cp1", name: "", locationId: "", type: "cp" }],
          // Buzzer timing must be loaded for the same reason as targetLoops
          // below: Save rewrites the whole categories array, so a flag the form
          // does not carry is erased on the next update — the toggle came back
          // off and the category silently reverted to a mat-timed race.
          isBuzzerTimed: cat.isBuzzerTimed || false,
          // Same reason: Save rewrites the categories array, so the precision
          // flag has to round-trip through the form or it is erased.
          showHundredths: cat.showHundredths === true,
          // Looping event config
          isLooping: cat.isLooping || false,
          isRelay: cat.isRelay || false,
          relayTimingMode: cat.relayTimingMode || (cat.isRelay ? "team_chip" : undefined),
          useIndividualStart:
            cat.individualStart || cat.useIndividualStart || false,
          minFinishMinutes: cat.minFinishMinutes || 0,
          startMatOffsetMinutes: cat.startMatOffsetMinutes || 0,
          // A distance category finishes at a lap count. Load it here or Save
          // silently drops it: this form rewrites the whole categories array,
          // so any field it does not carry is erased — which is how the 50K and
          // 42K targets were lost mid-race.
          targetLoops: cat.targetLoops || 0,
          lapDistanceMeters: cat.lapDistanceMeters ?? "",
        })),
      );
      setEventSaved(true);
      setMessage(`Event "${event.eventName}" loaded for editing.`);
    } else if (!selectedEventId) {
      setEventName("");
      setEventDate("");
      setEventStatus("ongoing");
      setEventImageUrl("");
      setLocation("");
      setLivetrackUrl("");
      setCategories([
        {
          id: "",
          name: "",
          startTime: "",
          cutOffTime: "",
          checkpoints: [{ id: "cp1", name: "", locationId: "", type: "cp" }],
        },
      ]);
      setEventSaved(false);
      setMessage("");
    }
  }, [selectedEventId, events, isAdmin, userTimeZone]);

  const handleCreateNewEvent = () => {
    setSelectedEventId("");
    setEventName("");
    setEventDate("");
    setEventStatus("ongoing");
    setEnableCertificate(false);
    setShowSplits(false);
    setEventImageFile(null);
    setEventImageUrl("");
    setLocation("");
    setLivetrackUrl("");
    setEventSlug("");
    setCategories([
      {
        id: "",
        name: "",
        startTime: "",
        cutOffTime: "",
        checkpoints: [{ id: "cp1", name: "", locationId: "", type: "cp" }],
        isLooping: false,
        isRelay: false,
        relayTimingMode: undefined,
        useIndividualStart: false,
        minFinishMinutes: 0,
        startMatOffsetMinutes: 0,
        targetLoops: 0,
        lapDistanceMeters: "",
      },
    ]);
    setEventSaved(false);
    setMessage("");
  };

  const handleAddCategory = () => {
    setCategories([
      ...categories,
      {
        id: "",
        name: "",
        startTime: "",
        cutOffTime: "",
        checkpoints: [{ id: "cp1", name: "", locationId: "", type: "cp" }],
      },
    ]);
    setMessage("New category added.");
  };

  const handleRemoveCategory = (index) => {
    setCategories(categories.filter((_, i) => i !== index));
    setMessage("Category removed.");
  };

  const handleAddCheckpoint = (categoryIndex) => {
    const newCategories = [...categories];
    const existingCheckpointIds = newCategories[categoryIndex].checkpoints.map(
      (cp) => cp.id,
    );
    let newCheckpointId = `cp${
      newCategories[categoryIndex].checkpoints.length + 1
    }`;
    let suffix = newCategories[categoryIndex].checkpoints.length + 1;
    while (existingCheckpointIds.includes(newCheckpointId)) {
      newCheckpointId = `cp${suffix}`;
      suffix++;
    }
    newCategories[categoryIndex].checkpoints.push({
      id: newCheckpointId,
      name: "",
      locationId: "",
      type: "cp",
    });
    setCategories(newCategories);
    setMessage(
      `Checkpoint ${newCheckpointId} added to category ${
        newCategories[categoryIndex].name || `Category ${categoryIndex + 1}`
      }.`,
    );
  };

  const handleRemoveCheckpoint = (categoryIndex, checkpointIndex) => {
    const newCategories = [...categories];
    const removedCheckpoint =
      newCategories[categoryIndex].checkpoints[checkpointIndex];
    newCategories[categoryIndex].checkpoints = newCategories[
      categoryIndex
    ].checkpoints.filter((_, i) => i !== checkpointIndex);
    setCategories(newCategories);
    setMessage(
      `Checkpoint ${removedCheckpoint.id} removed from category ${
        newCategories[categoryIndex].name || `Category ${categoryIndex + 1}`
      }.`,
    );
  };

  const handleCheckpointIdChange = (categoryIndex, checkpointIndex, newId) => {
    if (!newId) {
      setMessage("Checkpoint ID cannot be empty.");
      return;
    }
    const existingCheckpointIds = categories[categoryIndex].checkpoints
      .filter((_, cpIdx) => cpIdx !== checkpointIndex)
      .map((cp) => cp.id);
    if (existingCheckpointIds.includes(newId)) {
      setMessage(
        `Checkpoint ID "${newId}" already exists in this category. Please use a unique ID within the category.`,
      );
      return;
    }
    const newCategories = [...categories];
    newCategories[categoryIndex].checkpoints[checkpointIndex].id = newId;
    setCategories(newCategories);
    setMessage("");
  };

  const handleCheckpointLocationIdChange = (
    categoryIndex,
    checkpointIndex,
    newLocationId,
  ) => {
    if (!newLocationId) {
      setMessage("Location ID cannot be empty.");
      return;
    }
    const newCategories = [...categories];
    newCategories[categoryIndex].checkpoints[checkpointIndex].locationId =
      newLocationId.toLowerCase().replace(/\s+/g, "_");
    setCategories(newCategories);
    setMessage("");
  };

  const handleStatusChange = (newStatus) => {
    setEventStatus(newStatus);
    setMessage(`Event status changed to "${newStatus.toUpperCase()}".`);
  };

  // === PER-CATEGORY PARTICIPANT UPLOAD ===
  const uploadCategoryParticipants = async (
    eventId,
    file,
    categoryId,
    categoryName,
  ) => {
    if (!eventId) {
      showNotification(
        "error",
        "Save the event before uploading participants.",
      );
      return;
    }

    setUploadContext({ scope: "participant", categoryId });
    setUploadTone("info");
    setIsUploading(true);
    setUploadProgress(0);
    setUploadMessage(`Reading CSV for category ${categoryName}…`);

    const validNationalities = [
      // Asia
      "indonesia",
      "ina",
      "republik indonesia",
      "wni",
      "malaysia",
      "my",
      "mys",
      "singapore",
      "sgp",
      "sg",
      "thailand",
      "thai",
      "tha",
      "japan",
      "japanese",
      "jpn",
      "jp",
      "south korea",
      "korea",
      "korean",
      "kor",
      "kr",
      "china",
      "chinese",
      "chn",
      "cn",
      "taiwan",
      "tpe",
      "tw",
      "india",
      "ind",
      "in",
      // Europe
      "france",
      "french",
      "fra",
      "fr",
      "perancis",
      "prancis",
      "germany",
      "german",
      "ger",
      "de",
      "deu",
      "italy",
      "italian",
      "ita",
      "it",
      "spain",
      "spanish",
      "esp",
      "es",
      "russia",
      "russian",
      "rus",
      "ru",
      "united kingdom",
      "uk",
      "gbr",
      "gb",
      "british",
      "england",
      "english",
      "netherlands",
      "dutch",
      "ned",
      "nl",
      "belgium",
      "belgian",
      "bel",
      "be",
      "sweden",
      "swedish",
      "swe",
      "se",
      "norway",
      "norwegian",
      "nor",
      "no",
      "finland",
      "finnish",
      "fin",
      "fi",
      "denmark",
      "danish",
      "den",
      "dk",
      "austria",
      "austrian",
      "aut",
      "at",
      "hungary",
      "hungarian",
      "hun",
      "hu",
      "czech republic",
      "czech",
      "cze",
      "cz",
      "belarus",
      "blr",
      "by",
      "sint maarten",
      "sint maarten (dutch part)",
      "sxm",
      // Americas
      "australia",
      "australian",
      "aus",
      "au",
      "canada",
      "canadian",
      "can",
      "ca",
      "dominican republic",
      "dom",
      "do",
      // Africa & Middle East
      "morocco",
      "moroccan",
      "mar",
      "ma",
      "egypt",
      "egyptian",
      "egy",
      "eg",
      // Fallback
      "n/a",
      "",
    ];

    return new Promise((resolve) => {
      const reader = new FileReader();

      reader.onload = async (e) => {
        try {
          // 1. Parsing CSV
          const text = e.target.result;
          const lines = text
            .split(/\r?\n/)
            .map((l) => l.trim())
            .filter((l) => l);

          if (lines.length === 0) {
            setUploadTone("error");
            setUploadMessage("CSV is empty.");
            setIsUploading(false);
            resolve();
            return;
          }

          // Detect separator — prefer TAB because names may contain commas.
          const firstLine = lines[0];
          const tabCount = (firstLine.match(/\t/g) || []).length;
          const commaCount = (firstLine.match(/,/g) || []).length;

          // Use TAB if header has tabs, otherwise use comma
          let separator = "\t";
          if (tabCount === 0 && commaCount > 0) {
            separator = ",";
          }
          console.log(
            `[CSV] Detected separator: ${separator === "\t" ? "TAB" : "COMMA"}, tabs=${tabCount}, commas=${commaCount}`,
          );

          // Comma files are re-parsed from the raw text with a quote-aware
          // reader. Splitting on "," alone corrupts any quoted field that
          // contains a comma ("SEÑORITA DK, JR"), which silently shifts every
          // later column — BIB lands in Name, chip codes land nowhere.
          const splitRow = (line) => line.split(separator).map((c) => c.trim());
          let rowsFromQuoted = null;
          if (separator === ",") {
            rowsFromQuoted = [];
            let row = [];
            let cur = "";
            let inQuotes = false;
            for (let i = 0; i < text.length; i++) {
              const ch = text[i];
              if (inQuotes) {
                if (ch === '"') {
                  if (text[i + 1] === '"') {
                    cur += '"';
                    i++;
                  } else inQuotes = false;
                } else cur += ch;
              } else if (ch === '"') inQuotes = true;
              else if (ch === ",") {
                row.push(cur.trim());
                cur = "";
              } else if (ch === "\n") {
                row.push(cur.trim());
                cur = "";
                rowsFromQuoted.push(row);
                row = [];
              } else if (ch !== "\r") cur += ch;
            }
            if (cur !== "" || row.length) {
              row.push(cur.trim());
              rowsFromQuoted.push(row);
            }
            rowsFromQuoted = rowsFromQuoted.filter((r) =>
              r.some((c) => c !== ""),
            );
          }

          // Validate header
          const header = (
            rowsFromQuoted ? rowsFromQuoted[0] : firstLine.split(separator)
          ).map((h) => h.trim().toLowerCase());
          const expected = ["bib", "name", "gender", "nationality"];
          // Optional check: if categoryId exists, we ignore it or warn? We just check required cols.
          // We expect specifically these columns or subset? Let's be flexible but require minimal.
          // Required: bib, name, gender. Nationality optional.

          const headerMap = {};
          header.forEach((h, i) => (headerMap[h] = i));

          // Registration exports (WaniTrail, VolcanoTrail, TamboraJungle) label
          // the same data differently: "BIB NUMBER" instead of "bib", "FULL NAME"
          // instead of "name". Map those onto the canonical keys so a registration
          // export imports as-is. Canonical headers already present always win, so
          // existing CSVs are unaffected.
          const HEADER_ALIASES = {
            bib: ["bib number", "bibnumber", "bib_number", "no bib", "nobib"],
            // "nama lengkap" is what the registration export actually ships —
            // Sembalun's sheets were rejected because only the bare "nama" was
            // listed here, so bib and gender matched and the name did not.
            name: [
              "full name",
              "fullname",
              "full_name",
              "nama",
              "nama lengkap",
              "namalengkap",
              "nama_lengkap",
              "participant name",
            ],
            gender: ["jenis kelamin", "sex", "jeniskelamin", "jenis_kelamin"],
            nationality: ["country", "negara", "kewarganegaraan"],
            dateofbirth: ["birth date", "birthdate", "birth_date", "tanggal lahir", "tanggallahir", "tanggal_lahir"],
            chipcode: ["chip code", "chip_code", "tag code", "tagcode"],
            race: ["category", "kategori", "category name", "categoryname"],
          };
          for (const [canonical, aliases] of Object.entries(HEADER_ALIASES)) {
            if (headerMap[canonical] !== undefined) continue;
            for (const alias of aliases) {
              if (headerMap[alias] !== undefined) {
                headerMap[canonical] = headerMap[alias];
                break;
              }
            }
          }

          if (
            headerMap["bib"] === undefined ||
            headerMap["name"] === undefined ||
            headerMap["gender"] === undefined
          ) {
            setUploadTone("error");
            setUploadMessage(
              `Invalid header. Required: bib, name, gender — or a registration export using BIB NUMBER, FULL NAME, GENDER. (Optional: nationality/COUNTRY, age, dateofbirth/BIRTH DATE, chipcode/CHIPCODE, race/CATEGORY)`,
            );
            showNotification(
              "error",
              "CSV header must include bib, name, gender (nationality/age optional).",
            );
            setIsUploading(false);
            resolve();
            return;
          }

          // 2. Process rows
          const dataRows = rowsFromQuoted
            ? rowsFromQuoted.slice(1)
            : lines.slice(1);
          setUploadMessage(
            `Processing ${dataRows.length} participants for ${categoryName}…`,
          );

          const participants = [];
          const errors = [];

          dataRows.forEach((row, idx) => {
            const rowNum = idx + 2;

            // Rows arrive pre-split when the quote-aware reader ran; otherwise
            // they are still raw text.
            const isPreSplit = Array.isArray(row);

            // Skip empty or whitespace-only rows silently
            if (!row || (!isPreSplit && row.trim() === "")) {
              return;
            }

            const cols = isPreSplit ? row : splitRow(row);
            if (cols.every((c) => c === "")) return;

            // Map columns based on header
            const bib = cols[headerMap["bib"]] || "";
            const name = cols[headerMap["name"]] || "";
            const genderRaw = cols[headerMap["gender"]] || "";
            const nationalityInput =
              headerMap["nationality"] !== undefined
                ? cols[headerMap["nationality"]]
                : "N/A";
            const ageInput =
              headerMap["age"] !== undefined ? cols[headerMap["age"]] : "";
            const dobInput =
              headerMap["dateofbirth"] !== undefined
                ? cols[headerMap["dateofbirth"]]
                : headerMap["dob"] !== undefined
                  ? cols[headerMap["dob"]]
                  : "";
            // Optional: MYLAPS transponder chip code (7-char alphanumeric). Used by mylaps-ingest for chip→BIB reverse lookup.
            const chipCodeRaw =
              headerMap["chipcode"] !== undefined
                ? cols[headerMap["chipcode"]]
                : headerMap["chip_code"] !== undefined
                  ? cols[headerMap["chip_code"]]
                  : headerMap["chip"] !== undefined
                    ? cols[headerMap["chip"]]
                    : "";
            // Optional: race label — coarse category identifier used by the
            // shared MYLAPS Results CSV (one file for entire event) to route
            // each row into the correct category at import time. Accepts
            // aliases so operators can author either "race" or "racename".
            const raceRaw =
              headerMap["race"] !== undefined
                ? cols[headerMap["race"]]
                : headerMap["racename"] !== undefined
                  ? cols[headerMap["racename"]]
                  : headerMap["event"] !== undefined
                    ? cols[headerMap["event"]]
                    : "";

            // Optional: relay team columns. Two runners sharing a teamId are
            // scored as one entry (see rankRelayTeams in routes/timing.js).
            // Absent for every non-relay event, which keeps existing CSVs valid.
            const teamIdRaw =
              headerMap["teamid"] !== undefined
                ? cols[headerMap["teamid"]]
                : headerMap["team_id"] !== undefined
                  ? cols[headerMap["team_id"]]
                  : headerMap["team"] !== undefined
                    ? cols[headerMap["team"]]
                    : "";
            const teamNameRaw =
              headerMap["teamname"] !== undefined
                ? cols[headerMap["teamname"]]
                : headerMap["team_name"] !== undefined
                  ? cols[headerMap["team_name"]]
                  : "";

            // Skip completely empty rows (all key fields empty)
            if (!bib.trim() && !name.trim() && !genderRaw.trim()) {
              return; // Silent skip
            }

            // A registration sheet reserves a block of spare bibs at the end of
            // every category: the bib is printed and numbered, but nobody holds
            // it yet, so the name reads "BIB KOSONG" (or is blank) and there is
            // no gender. These are placeholders, not broken rows — reporting
            // them as errors buried the real failures in a wall of noise.
            // Reserve bibs are added for real when someone is assigned one.
            const namePlaceholder =
              !name.trim() || name.trim().toUpperCase() === "BIB KOSONG";
            if (namePlaceholder && !genderRaw.trim()) {
              return; // Silent skip
            }

            if (!bib.trim() || !name.trim()) {
              errors.push(`Row ${rowNum}: BIB or Name is empty`);
              return;
            }

            // Normalize gender - accept more formats
            const genderLower = (genderRaw || "").toLowerCase().trim();
            let gender = null;
            if (
              ["male", "m", "laki-laki", "laki", "pria", "l"].includes(
                genderLower,
              )
            ) {
              gender = "male";
            } else if (
              ["female", "f", "perempuan", "wanita", "w", "p"].includes(
                genderLower,
              )
            ) {
              gender = "female";
            }

            if (!gender) {
              errors.push(
                `Row ${rowNum}: Gender "${genderRaw}" is invalid (use Male/Female/M/F)`,
              );
              return;
            }

            const natInput = nationalityInput || "N/A";
            const nationality = natInput === "" ? "N/A" : natInput.trim();

            // Age is optional - if empty or invalid, set to null
            let age = null;
            if (ageInput && ageInput.trim() !== "") {
              const parsedAge = parseInt(ageInput.trim());
              if (!isNaN(parsedAge) && parsedAge >= 0) {
                age = parsedAge;
              }
              // Don't error on invalid age, just skip it
            }
            // Parse the birth date whenever the column is present. It was only
            // ever read as a fallback for a missing age and then discarded, so
            // the date itself never reached Firestore and could not be exported
            // — age alone cannot reconstruct it, and age categories are usually
            // settled on the date of birth, not a rounded year.
            let birthDate = null;
            if (dobInput && dobInput.trim() !== "") {
              const birthMoment = moment(
                dobInput.trim(),
                [
                  "DD-MM-YYYY",
                  "MM-DD-YYYY",
                  "YYYY-MM-DD",
                  "DD/MM/YYYY",
                  "MM/DD/YYYY",
                  "YYYY/MM/DD",
                ],
                true,
              );
              if (birthMoment.isValid()) {
                // ISO date, not the raw string: the CSV may arrive in any of the
                // formats above and a stable shape keeps sorting and age maths
                // predictable downstream.
                birthDate = birthMoment.format("YYYY-MM-DD");
                if (age === null) age = moment().diff(birthMoment, "years");
              }
            }

            const chipCode = (chipCodeRaw || "").trim();
            const teamId = (teamIdRaw || "").trim();
            const teamName = (teamNameRaw || "").trim();
            const race = (raceRaw || "").toString().trim();
            participants.push({
              bib: bib.trim(),
              name: name.trim(),
              gender,
              categoryId: categoryId.toLowerCase().trim(),
              nationality,
              age,
              eventId,
              // Kept as its own field: age is derived and drifts every birthday,
              // while the date of birth is what an age-category protest is
              // settled on.
              ...(birthDate ? { birthDate } : {}),
              // Only include chipCode when provided — keeps legacy CSVs untouched.
              ...(chipCode ? { chipCode } : {}),
              // Race label from the CSV `race` column (e.g. "42", "10K"). Stored
              // verbatim; MYLAPS Results CSV import uses it to route rows into
              // the correct category when a single exporter file covers the
              // whole event.
              ...(race ? { race } : {}),
              // Relay: teamId groups runners; teamName is what the results page
              // shows for the team row. Omitted entirely when not a relay event.
              ...(teamId ? { teamId } : {}),
              ...(teamName ? { teamName } : {}),
            });
          });

          if (participants.length === 0) {
            setUploadTone("error");
            setUploadMessage(`No valid participants to upload.`);
            setIsUploading(false);
            resolve();
            return;
          }

          if (errors.length > 0) {
            console.error("CSV Errors:", errors);
            alert(
              `${errors.length} row(s) skipped:\n\n${errors.slice(0, 10).join("\n")}${errors.length > 10 ? `\n…and ${errors.length - 10} more` : ""}`,
            );
            showNotification(
              "warning",
              `${errors.length} row(s) skipped. See console for details.`,
            );
          }

          // 3. Upload
          setUploadMessage(
            `Uploading ${participants.length} participants to ${categoryName}…`,
          );

          const res = await authenticatedFetch(
            `${import.meta.env.VITE_API_BASE_URL}/api/timing/upload-participants`,
            {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({
                eventId,
                participants,
                categories, // We still pass all categories for validation context backend side
              }),
            },
          );

          const data = await res.json();

          if (res.ok) {
            setUploadTone("success");
            setUploadMessage(
              `Success — ${data.uploaded} participants added to ${categoryName}.`,
            );
            showNotification(
              "success",
              `Upload finished for ${categoryName}: ${data.uploaded} participants.`,
            );
            setUploadProgress(100);
          } else {
            setUploadTone("error");
            setUploadMessage(`Upload failed: ${data.message}`);
            showNotification("error", `Upload failed: ${data.message}`);
          }

          setIsUploading(false);
          resolve();
        } catch (err) {
          setUploadTone("error");
          setUploadMessage("Critical error: " + err.message);
          setIsUploading(false);
          resolve();
        }
      };
      reader.readAsText(file);
    });
  };

  // === UPLOAD RESULTS FILE (CSV or XLSX) ===
  // Two usage modes:
  //   1. MYLAPS T&S CSV export — flat CSV with the column aliases listed below.
  //   2. Backup/restore round-trip — re-uploading the XLSX produced by
  //      `generateAndDownloadExcel`. In that case the hidden `_raw` sheet is
  //      read directly (one row per checkpoint per runner, ISO timestamps).
  //
  // Column aliases (CSV and _raw both go through this map):
  //   bib        ← bib|bibnumber|bib_number|nr|startnumber
  //   chipCode   ← chipcode|chip|chip_code|tagcode|tag
  //   gunTime    ← guntime|gun_time|officialtime|gun
  //   netTime    ← chiptime|chip_time|nettime|net_time|net
  //   finishTime ← time|finishtime|timestamp (ISO, e.g. 2026-04-24T07:32:15.347Z)
  //   finishTimeWallClock ← wallclock|wall_time|hh:mm:ss (server composes with event date)
  //   location   ← location|loop|loopid|checkpoint|cp
  //   race       ← race|racename|event|category|categoryname
  const RESULTS_COL_ALIASES = {
    bib: ["bib", "bibnumber", "nr", "startnumber"],
    chipCode: ["chipcode", "chip", "tagcode", "tag"],
    gunTime: ["guntime", "officialtime", "gun"],
    netTime: ["chiptime", "nettime", "net"],
    finishTime: ["time", "finishtime", "timestamp", "datetime"],
    finishTimeWallClock: ["wallclock", "walltime", "clocktime"],
    location: ["location", "loop", "loopid", "checkpoint", "cp"],
    // Race label used by the backend to validate/route each row into the
    // correct category when the exporter writes a single file for the whole
    // event (the typical MYLAPS T&S layout).
    race: ["race", "racename", "event", "category", "categoryname"],
  };

  // Normalises one record (whose keys may be arbitrary header labels) into the
  // canonical shape expected by POST /api/timing/import-results.
  const normaliseResultRow = (raw) => {
    const norm = {};
    Object.entries(raw).forEach(([k, v]) => {
      const key = String(k).trim().toLowerCase().replace(/[\s_]/g, "");
      norm[key] = v;
    });
    const out = {};
    Object.entries(RESULTS_COL_ALIASES).forEach(([canonical, alts]) => {
      for (const a of alts) {
        if (norm[a] !== undefined && norm[a] !== null && norm[a] !== "") {
          out[canonical] = String(norm[a]).trim();
          break;
        }
      }
    });
    return out;
  };

  const uploadResultsFile = async (eventId, file) => {
    if (!eventId) {
      showNotification("error", "Save the event first.");
      return;
    }
    setUploadContext({ scope: "results" });
    setUploadTone("info");
    setIsUploading(true);
    setUploadProgress(0);

    const ext = (file.name.split(".").pop() || "").toLowerCase();
    const isXlsx = ext === "xlsx" || ext === "xls";
    setUploadMessage(isXlsx ? "Reading results XLSX…" : "Reading results CSV…");

    const parseXlsx = async () => {
      const buf = await file.arrayBuffer();
      const wb = XLSX.read(buf, { type: "array" });
      // Prefer the lossless backup sheet written by generateAndDownloadExcel.
      // Fall back to the first sheet for arbitrary Excel files.
      const sheetName = wb.SheetNames.includes("_raw")
        ? "_raw"
        : wb.SheetNames.find((n) => /^raw$/i.test(n)) || wb.SheetNames[0];
      if (!sheetName) return [];
      const ws = wb.Sheets[sheetName];
      const rawRows = XLSX.utils.sheet_to_json(ws, { defval: "" });
      return rawRows.map(normaliseResultRow);
    };

    const parseCsv = async () => {
      const text = await file.text();
      const lines = text
        .split(/\r?\n/)
        .map((l) => l.trim())
        .filter(Boolean);
      if (lines.length < 2) return [];
      const separator = lines[0].split("\t").length > 1 ? "\t" : ",";
      const header = lines[0].split(separator).map((h) => h.trim());
      return lines.slice(1).map((line) => {
        const cols = line.split(separator).map((c) => c.trim());
        const obj = {};
        header.forEach((h, i) => (obj[h] = cols[i] || ""));
        return normaliseResultRow(obj);
      });
    };

    try {
      const rows = (isXlsx ? await parseXlsx() : await parseCsv()).filter(
        (r) => r.bib || r.chipCode,
      );

      if (rows.length === 0) {
        setUploadTone("error");
        setUploadMessage(
          isXlsx
            ? "No rows found. Expected a `_raw` sheet (from Export Excel) or a sheet with bib/chipCode columns."
            : "CSV is empty or missing BIB/ChipCode column.",
        );
        showNotification("error", "No importable rows found.");
        setIsUploading(false);
        return;
      }

      setUploadMessage(`Uploading ${rows.length} result row(s)…`);
      const res = await authenticatedFetch(
        `${import.meta.env.VITE_API_BASE_URL}/api/timing/import-results`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ eventId, results: rows }),
        },
      );
      const data = await res.json();
      if (res.ok) {
        setUploadTone("success");
        setUploadMessage(
          `Import finished: ${data.imported} imported, ${data.skipped} skipped.`,
        );
        showNotification("success", `${data.imported} result row(s) imported.`);
        if (data.errors?.length) {
          console.warn("[Results Import Errors]", data.errors);
          alert(`${data.skipped} row(s) skipped:\n\n${data.errors.join("\n")}`);
        }
        setUploadProgress(100);
      } else {
        setUploadTone("error");
        setUploadMessage(`Import failed: ${data.message}`);
        showNotification("error", `Import failed: ${data.message}`);
      }
    } catch (err) {
      setUploadTone("error");
      setUploadMessage("Critical error: " + err.message);
      showNotification("error", err.message);
    } finally {
      setIsUploading(false);
    }
  };

  // === HANDLE SAVE EVENT ===
  const handleSaveEvent = async () => {
    if (!eventName || !eventDate) {
      setMessage("Event name and date are required.");
      return;
    }
    // A buzzer-timed category has no mass start, no cut-off and no mats: the
    // athlete's own buzzer press starts and stops the clock, and the operator
    // types the elapsed time in directly. Demanding those fields would only
    // force invented values into the record, so they are exempt — everything
    // else still has to be complete.
    if (
      categories.some(
        (cat) =>
          !cat.id ||
          !cat.name ||
          (!cat.isBuzzerTimed && (!cat.startTime || !cat.cutOffTime)),
      )
    ) {
      setMessage(
        "All categories must have ID and name, plus start time and cut off time unless buzzer-timed.",
      );
      return;
    }
    // Buzzer-timed categories are exempt: the athlete's own buzzer press starts
    // and stops the clock, so there are no mats to describe. Demanding a
    // checkpoint here forced operators to invent one that nothing ever reads.
    if (
      categories.some(
        (cat) =>
          !cat.isBuzzerTimed &&
          cat.checkpoints.some((cp) => !cp.id || !cp.name || !cp.locationId),
      )
    ) {
      setMessage("All checkpoints must have ID, name, and location ID.");
      return;
    }

    for (const cat of categories) {
      // Nothing to check: a buzzer-timed category legitimately has neither.
      if (cat.isBuzzerTimed) continue;
      const startTime = moment.tz(cat.startTime, userTimeZone);
      const cutOffTime = moment.tz(cat.cutOffTime, userTimeZone);
      if (!startTime.isValid() || !cutOffTime.isValid()) {
        setMessage(
          `Invalid start time or cut off time in category "${
            cat.name || cat.id
          }".`,
        );
        return;
      }
      if (cutOffTime.isSameOrBefore(startTime)) {
        setMessage(
          `Cut off time must be after start time in category "${
            cat.name || cat.id
          }".`,
        );
        return;
      }
    }

    for (const cat of categories) {
      const checkpointIds = cat.checkpoints.map((cp) => cp.id);
      const uniqueCheckpointIds = new Set(checkpointIds);
      if (uniqueCheckpointIds.size !== checkpointIds.length) {
        const duplicates = checkpointIds.filter(
          (id, index) => checkpointIds.indexOf(id) !== index,
        );
        setMessage(
          `Duplicate checkpoint IDs found in category "${
            cat.name || cat.id
          }": ${duplicates.join(", ")}.`,
        );
        return;
      }
    }

    isSaving.current = true;
    setMessage("Saving event...");
    try {
      // An event's internal ID is immutable once created. Editing its display
      // name or date must update the same database document.
      const eventId =
        selectedEventId ||
        `${eventName
          .trim()
          .toLowerCase()
          .replace(/[^a-z0-9]+/g, "_")
          .replace(/^_+|_+$/g, "")}_${new Date(eventDate).getFullYear()}`;

      let heroImage = eventImageUrl;

      if (eventImageFile) {
        setMessage("Uploading event image to Firebase Storage...");
        const storage = getStorage();
        // Updated to match storage.rules: /event-images/{eventId}/{fileName}
        const imageRef = ref(
          storage,
          `event-images/${eventId}/${eventImageFile.name}`,
        );
        await uploadBytes(imageRef, eventImageFile);
        heroImage = await getDownloadURL(imageRef);
      }

      // Certificate background follows the same storage path convention as the
      // hero image, so both live under the event's own folder.
      let certBackgroundUrl = certificateBackground;
      if (certificateBgFile) {
        setMessage("Uploading certificate background...");
        const storage = getStorage();
        const certRef = ref(
          storage,
          `event-images/${eventId}/cert-${certificateBgFile.name}`,
        );
        await uploadBytes(certRef, certificateBgFile);
        certBackgroundUrl = await getDownloadURL(certRef);
      }

      // Use full URL as livetrackSlug (legacy naming)
      let livetrackSlug = livetrackUrl ? livetrackUrl.trim() : "";

      const eventData = {
        eventId,
        eventName,
        date: eventDate,
        status: eventStatus,
        enableCertificate,
        enableAgeGroup,
        showSplits,
        // Sent only when configured; omitting it leaves the default in place.
        ...(ageGroups.length ? { ageGroups } : {}),
        certificateBackground: certBackgroundUrl,
        // Omitted when untouched so events keep rendering with the defaults.
        ...(certificateLayout ? { certificateLayout } : {}),
        heroImage,
        location,
        livetrackSlug,
        categories: categories.map((cat) => ({
          id: cat.id,
          name: cat.name,
          // Null rather than an Invalid Date string: a buzzer-timed category
          // has no start or cut-off, and writing "Invalid date" would make
          // every reader of these fields fail in a different way.
          startTime: cat.startTime
            ? moment.tz(cat.startTime, userTimeZone).toISOString()
            : null,
          cutOffTime: cat.cutOffTime
            ? moment.tz(cat.cutOffTime, userTimeZone).toISOString()
            : null,
          // A buzzer-timed category has no mats. The form still carries a blank
          // starter row, and sending it would fail the API's "checkpoint must
          // have id, name and locationId" check — so it is dropped here rather
          // than stored as an empty mat nothing will ever read.
          checkpoints: cat.isBuzzerTimed ? [] : cat.checkpoints,
          isBuzzerTimed: cat.isBuzzerTimed || false,
          showHundredths: cat.isBuzzerTimed ? cat.showHundredths === true : false,
          // Looping event config
          isLooping: cat.isLooping || false,
          isRelay: cat.isLooping ? cat.isRelay || false : undefined,
          relayTimingMode:
            cat.isLooping && cat.isRelay
              ? (cat.relayTimingMode || "team_chip")
              : undefined,
          individualStart: cat.isLooping ? false : cat.useIndividualStart || false,
          ...(cat.minFinishMinutes > 0
            ? { minFinishMinutes: cat.minFinishMinutes }
            : {}),
          ...(cat.startMatOffsetMinutes > 0
            ? { startMatOffsetMinutes: cat.startMatOffsetMinutes }
            : {}),
          // Only looping distance categories carry a target. A timed category
          // (24H/12H/6H) ends at its cut-off, and a ceiling there would cut a
          // runner's race short, so the field is omitted rather than zeroed.
          ...(cat.isLooping && Number(cat.lapDistanceMeters) > 0
            ? { lapDistanceMeters: Number(cat.lapDistanceMeters) }
            : {}),
          ...(cat.isLooping && cat.targetLoops > 0
            ? { targetLoops: Number(cat.targetLoops) }
            : {}),
        })),
        slug: eventSlug, // Save the slug in the event document
      };

      const saveResponse = await authenticatedFetch(
        `${import.meta.env.VITE_API_BASE_URL}/api/timing/save-event`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(eventData),
        },
      );

      const saveData = await saveResponse.json();
      if (!saveResponse.ok) {
        setMessage(
          `Error saving event: ${saveData.error || "Failed to save event."}`,
        );
        setEventSaved(false);
        isSaving.current = false;
        return;
      }

      // The layout is now on the server, so the local draft has nothing left to
      // protect — keeping it would silently re-apply stale numbers on reload.
      clearCertDraft();

      const successMessage = `Event "${eventName}" ${
        selectedEventId ? "updated" : "created"
      } successfully!`;
      setMessage(successMessage);
      setEventSaved(true);

      setEvents(await loadEvents());
      setSelectedEventId(eventId);

      isSaving.current = false;
    } catch (error) {
      setMessage("Error saving event: " + error.message);
      setEventSaved(false);
      isSaving.current = false;
    }
  };
  // Fetch every result row for an event.
  //
  // The endpoint is paginated (default 100, hard cap 500), so a bare call
  // returned only the first page — 100 rows spread across every category,
  // which looked like "10K has 35 finishers" when it had 300+. Walk every page
  // and concatenate. Shared by the Excel and podium exports so the two can
  // never disagree about how much of the field they saw.
  const fetchAllResults = async (eventId) => {
    const LIMIT = 500;
    const base = `${import.meta.env.VITE_API_BASE_URL}/api/timing/results?eventId=${eventId}&limit=${LIMIT}`;
    const first = await fetch(`${base}&page=1`);
    if (!first.ok) throw new Error(`HTTP ${first.status}`);
    const data = await first.json();
    const totalPages = Number(data.totalPages) || 1;

    for (let p = 2; p <= totalPages; p++) {
      const res = await fetch(`${base}&page=${p}`);
      if (!res.ok) throw new Error(`HTTP ${res.status} on page ${p}`);
      const chunk = await res.json();
      data.results = data.results.concat(chunk.results || []);
    }

    // A short page count means rows went missing between requests; exporting a
    // silently truncated result sheet is worse than failing loudly.
    if (data.total && data.results.length < data.total) {
      throw new Error(
        `Expected ${data.total} results but fetched ${data.results.length}`,
      );
    }

    return data.results || [];
  };

  // === HANDLE EXPORT EXCEL (XLSX) ===
  const handleExportExcel = async () => {
    if (!selectedEventId) return;
    setMessage(`Exporting data for event: ${selectedEventId}...`);
    try {
      const data = { results: await fetchAllResults(selectedEventId) };

      if (!data.results || data.results.length === 0) {
        showNotification("warning", "No results found to export.");
        setMessage("No results found.");
        return;
      }

      // Delegate logic to utility
      generateAndDownloadExcel(data.results, categories, selectedEventId);

      // Auto-clear status banner after 3s; notifications are suppressed on export success to avoid a modal flash.
      setTimeout(() => setMessage(""), 3000);
    } catch (error) {
      console.error("Export failed:", error);
      setMessage("Export failed: " + error.message);
      showNotification("error", "Export failed");
    }
  };

  // === HANDLE EXPORT PODIUM (A4 PDF) ===
  // Top 10 per gender per category, for the prize desk. Unlike the Excel
  // export this is a printout, so it is deliberately limited to finishers —
  // see the note on `isFinisher` in podiumExport.
  const handleExportPodium = async () => {
    if (!selectedEventId) return;
    setMessage("Building podium recap…");
    try {
      const results = await fetchAllResults(selectedEventId);

      if (results.length === 0) {
        showNotification("warning", "No results found to export.");
        setMessage("No results found.");
        return;
      }

      generateAndDownloadPodiumPdf(
        results,
        categories,
        eventName || selectedEventId,
        selectedEventId,
        PODIUM_PLACES,
      );

      setMessage("Podium recap downloaded.");
      setTimeout(() => setMessage(""), 3000);
    } catch (error) {
      console.error("Podium export failed:", error);
      setMessage("Podium export failed: " + error.message);
      showNotification("error", error.message || "Podium export failed");
    }
  };

  // === HANDLE EXPORT ITRA (XLSX) ===
  // The submission workbook: one sheet per category, ranked finishers only,
  // in the column layout and country codes ITRA reads.
  const handleExportItra = async () => {
    if (!selectedEventId) return;
    setMessage("Building ITRA workbook…");
    try {
      const results = await fetchAllResults(selectedEventId);
      if (results.length === 0) {
        showNotification("warning", "No results found to export.");
        setMessage("No results found.");
        return;
      }

      const { sheets, unmapped } = generateAndDownloadItraXlsx(
        results,
        categories,
        selectedEventId,
      );

      // A blank birthdate is the row ITRA rejects, so say so at export time
      // rather than letting it come back from them days later.
      const missing = sheets.reduce((n, s) => n + s.noBirthdate, 0);
      const total = sheets.reduce((n, s) => n + s.count, 0);
      if (unmapped.length) {
        showNotification(
          "warning",
          `Unrecognised nationality left as-is: ${unmapped.join(", ")}`,
        );
      }
      setMessage(
        missing > 0
          ? `ITRA workbook downloaded — ${total} finishers, ${missing} without a birthdate.`
          : `ITRA workbook downloaded — ${total} finishers.`,
      );
      setTimeout(() => setMessage(""), 6000);
    } catch (error) {
      console.error("ITRA export failed:", error);
      setMessage("ITRA export failed: " + error.message);
      showNotification("error", error.message || "ITRA export failed");
    }
  };

  // Every event the ingest considers a candidate. More than one is the
  // ambiguous state that makes routing unpredictable.
  const ongoingEvents = useMemo(
    () => (events || []).filter((event) => event.status === "ongoing"),
    [events],
  );

  // Routing verdict, in order of severity. `activeEventId` is the ingest's own
  // answer, so it outranks anything derived from the form.
  const routingStatus = useMemo(() => {
    if (ingestError)
      return {
        tone: "warn",
        label: "Unknown",
        title: "Cannot reach the timing ingest",
        detail: `Its target event could not be read: ${ingestError}. Passings may be going anywhere — resolve before the start.`,
      };
    if (!ingestHealth) return null;
    const active = ingestHealth.activeEventId;
    if (!active)
      return {
        tone: "danger",
        label: "No target",
        title: "The ingest has no active event — passings are rejected",
        detail:
          ongoingEvents.length > 1
            ? `${ongoingEvents.length} events are set to "ongoing", so the ingest refuses to guess. Set all but the racing event to "completed", then press Re-check.`
            : 'No event is set to "ongoing". Set the racing event to "ongoing" in admin, then press Re-check.',
      };
    if (selectedEventId && active !== selectedEventId)
      return {
        tone: "danger",
        label: "Wrong event",
        title: `Passings are being filed into "${active}"`,
        detail: `That is not the event open here (${selectedEventId}). Every crossing will land in the wrong race until this matches — set this event to "ongoing" (and the other to "completed"), then press Re-check.`,
      };
    if (ongoingEvents.length > 1)
      return {
        tone: "warn",
        label: "Ambiguous",
        title: `Targeting "${active}", but ${ongoingEvents.length} events are ongoing`,
        detail: `Ongoing: ${ongoingEvents.map((event) => event.eventId).join(", ")}. The ingest is resolving this by its configured default; if that default ever changes, routing silently moves. Set the others to "completed" before the start.`,
      };
    return {
      tone: "ok",
      label: "Correct",
      title: `Passings are being filed into "${active}"`,
      detail: "This matches the event open here and it is the only ongoing event.",
    };
  }, [ingestHealth, ingestError, ongoingEvents, selectedEventId]);

  const tcpStatus = !tcpChecked
    ? {
        label: "Not checked",
        title: "Connection has not been checked",
        description:
          "Check before the race to confirm the MYLAPS decoder can send passing data.",
        badge: "border-slate-200 bg-slate-100 text-slate-600",
        dot: "bg-slate-400",
      }
    : tcpFetchError
      ? {
          label: "Unavailable",
          title: "Monitor service cannot be reached",
          description: `The connection check failed: ${tcpFetchError}`,
          badge: "border-amber-200 bg-amber-50 text-amber-700",
          dot: "bg-amber-500",
        }
      : tcpClients.length > 0
        ? {
            label: "Connected",
            title: `${tcpClients.length} decoder${tcpClients.length > 1 ? "s" : ""} connected`,
            description:
              "The MYLAPS TCP/IP export connection is active and ready to send passing data.",
            badge: "border-emerald-200 bg-emerald-50 text-emerald-700",
            dot: "bg-emerald-500",
          }
        : {
            label: "Disconnected",
            title: "No decoder connected",
            description:
              "Enable TCP/IP Export in MYLAPS Timing & Scoring, then check the connection again.",
            badge: "border-red-200 bg-red-50 text-red-700",
            dot: "bg-red-500",
          };
  const tcpToolsEnabled = tcpChecked && !tcpFetchError && tcpClients.length > 0;
  const hasLoopingCategories = categories.some(
    (category) => category.isLooping,
  );
  const hasStandardCategories = categories.some(
    (category) => !category.isLooping && !category.isBuzzerTimed,
  );
  const hasBuzzerCategories = categories.some(
    (category) => category.isBuzzerTimed,
  );
  // Only an individual start needs a start-control page: with a mass start the
  // mat records everyone off one gun, and there is nothing for an operator to
  // declare. Here the mat cannot tell "setting off" from "walking past", so
  // someone has to name who is going.
  const hasIndividualStart = categories.some(
    (category) =>
      (category.individualStart || category.useIndividualStart) &&
      !category.isLooping &&
      !category.isBuzzerTimed,
  );
  // The photo/TV board only has something to show once a mat is configured
  // behind the finish line, so the link appears with the gate and not before.
  const hasPostFinishGate = categories.some((category) =>
    (category.checkpoints || []).some(
      (checkpoint) => (checkpoint.type || "").toLowerCase() === "postfinish",
    ),
  );
  const eventRouteKey = eventSlug || selectedEventId;
  const quickLinks = [
    ...(hasIndividualStart
      ? [{ label: "Start Control", href: `/${eventRouteKey}/start` }]
      : []),
    // One link per input page the event actually needs. A buzzer-only event
    // gets no mat-input link at all, because typing BIBs against the clock is
    // not how it is timed.
    ...(hasStandardCategories
      ? [{ label: "Input Page", href: `/${eventRouteKey}/input` }]
      : []),
    ...(hasLoopingCategories
      ? [{ label: "Loop Input", href: `/${eventRouteKey}/input-loop` }]
      : []),
    ...(hasBuzzerCategories
      ? [{ label: "Ninja Input", href: `/${eventRouteKey}/input-ninja` }]
      : []),
    { label: "Results Editor", href: `/${eventRouteKey}/editor` },
    // The event root selects standard, age-group, or looping results from
    // database configuration. Keep one canonical public results URL.
    { label: "Results Page", href: `/${eventRouteKey}` },
    { label: "Start List", href: `/${eventRouteKey}/startlist` },
    { label: "Announcer", href: `/${eventRouteKey}/announcer` },
    // What officers typed and what the mat read, kept 90 days (Fly's log lines
    // last hours).
    { label: "Log", href: `/${eventRouteKey}/log` },
    ...(hasLoopingCategories || hasBuzzerCategories
      ? [{ label: "Venue Display", href: `/${eventRouteKey}/display` }]
      : []),
    ...(hasPostFinishGate
      ? [{ label: "Photo Gate", href: `/${eventRouteKey}/gate` }]
      : []),
  ];

  // Newest event first, by the event's own date (the first category's start
  // when an older event has none). The date is in the label too: two editions
  // of the same race otherwise read as the same option.
  const eventOptions = useMemo(() => {
    const dayOf = (event) => {
      const raw = event?.date || event?.categories?.[0]?.startTime;
      const day = raw ? moment(raw?.toDate ? raw.toDate() : raw) : null;
      return day?.isValid() ? day : null;
    };
    return [...events]
      .map((event) => ({ event, day: dayOf(event) }))
      .sort((a, b) => (b.day?.valueOf() ?? -Infinity) - (a.day?.valueOf() ?? -Infinity))
      .map(({ event, day }) => ({
        value: event.eventId,
        label: `${event.eventName || event.eventId}${day ? ` — ${day.format("DD MMM YYYY")}` : ""}`,
      }));
  }, [events]);

  if (!isAdmin) return null;

  return (
    <Suspense fallback={<LoadingSkeleton />}>
      <motion.div
        className="min-h-screen bg-white flex flex-col items-center py-10 px-4 sm:px-6 lg:px-8"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.3 }}
      >
        <div className="w-full max-w-7xl">
          <motion.h1
            className="text-3xl md:text-4xl font-extrabold text-slate-900 tracking-tight font-oxanium mb-4"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            Event Configuration
          </motion.h1>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            <div className="mb-4 grid grid-cols-1 gap-4 md:grid-cols-2">
              <div className="space-y-2">
                <label className="block text-slate-700">
                  Create New Event
                </label>
                <Button
                  onClick={handleCreateNewEvent}
                  variant="muted"
                  color="slate"
                  fullWidth
                >
                  <FontAwesomeIcon icon={faPlus} /> New Event
                </Button>
              </div>

              <FormField
                label="Open Event"
                name="selectedEvent"
                type="select"
                value={selectedEventId}
                onChange={(e) => setSelectedEventId(e.target.value)}
                options={eventOptions}
                defaultOptionLabel="Select an event"
                wrapperClassName="w-full"
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Section title="Event Details">
                <div className="space-y-4">
                  <FormField
                    label="Event Name"
                    name="eventName"
                    value={eventName}
                    onChange={(e) => setEventName(e.target.value)}
                    placeholder="e.g., SM Marathon"
                  />

                  <FormField
                    label="Event Slug (Clean URL)"
                    name="eventSlug"
                    value={eventSlug}
                    onChange={(e) =>
                      setEventSlug(
                        e.target.value
                          .toLowerCase()
                          .replace(/[^a-z0-9]/g, ""),
                      )
                    }
                    startIcon={
                      <span className="border-r border-slate-200 pr-3 text-slate-500">
                        livetiming.../
                      </span>
                    }
                    placeholder="e.g., indonesiamasters2025"
                    className="pl-[9.5rem]"
                  />

                  <FormField
                    label="Location"
                    name="location"
                    value={location}
                    onChange={(e) => setLocation(e.target.value)}
                    placeholder="e.g., Jakarta, Indonesia"
                  />

                  <div className="space-y-1">
                    <div className="mb-2 flex items-center justify-between gap-3">
                      <label className="text-slate-700">Event Image</label>
                      {eventImageUrl && !eventImageFile && (
                        <span className="shrink-0 text-xs text-slate-400">
                          Current image:{" "}
                          <a
                            href={eventImageUrl}
                            target="_blank"
                            rel="noreferrer"
                            className="font-semibold text-slate-700 hover:underline"
                          >
                            View
                          </a>
                        </span>
                      )}
                    </div>
                    <label className="flex items-center gap-3 w-full px-4 py-2.5 text-sm border border-slate-200 rounded-xl bg-white cursor-pointer hover:bg-slate-50 transition-colors">
                      <span className="px-3 py-1 bg-slate-100 text-slate-700 rounded-xl text-xs font-medium">
                        Choose File
                      </span>
                      <span className="text-slate-400 truncate">
                        {eventImageFile
                          ? eventImageFile.name
                          : "No file chosen"}
                      </span>
                      <input
                        type="file"
                        accept="image/*"
                        className="hidden"
                        onChange={(e) =>
                          setEventImageFile(e.target.files?.[0] || null)
                        }
                      />
                    </label>
                  </div>
                </div>
              </Section>

              <Section title="Event Settings">
                <div className="space-y-4">
                  <FormField
                    label="Event Date"
                    name="eventDate"
                    type="date"
                    value={eventDate}
                    onChange={(e) => setEventDate(e.target.value)}
                  />

                  <div className="flex h-11 items-center justify-between gap-3 rounded-xl border border-slate-200 bg-slate-50 px-3">
                    <div className="flex min-w-0 items-center gap-2">
                      <span className="text-xs font-semibold text-slate-700">
                        Certificate
                      </span>
                      <button
                        type="button"
                        onClick={() => setEnableCertificate(!enableCertificate)}
                        className={`relative inline-flex h-5 w-10 items-center rounded-full transition-colors ${
                          enableCertificate ? "bg-teal-500" : "bg-slate-300"
                        }`}
                        aria-pressed={enableCertificate}
                      >
                        <span
                          className={`absolute top-0.5 left-0.5 h-4 w-4 rounded-full bg-white shadow transition-transform ${
                            enableCertificate
                              ? "translate-x-5"
                              : "translate-x-0"
                          }`}
                        ></span>
                        <span className="sr-only">Toggle certificate</span>
                      </button>
                      {enableCertificate && (
                        <button
                          type="button"
                          onClick={() => setShowCertDesigner(true)}
                          className="ml-1 shrink-0 rounded-full border border-slate-200 bg-white px-2.5 py-0.5 text-[11px] font-medium text-slate-600 hover:bg-slate-50"
                        >
                          Design
                        </button>
                      )}

                      {/* Age group results. The public page groups and ranks by
                          age band as well as overall, which until now was only
                          reachable by matching the event id in the router. */}
                      <span className="ml-3 text-xs font-semibold text-slate-700">
                        Age Group
                      </span>
                      <button
                        type="button"
                        onClick={() => setEnableAgeGroup(!enableAgeGroup)}
                        className={`relative inline-flex h-5 w-10 items-center rounded-full transition-colors ${
                          enableAgeGroup ? "bg-teal-500" : "bg-slate-300"
                        }`}
                        aria-pressed={enableAgeGroup}
                      >
                        <span
                          className={`absolute top-0.5 left-0.5 h-4 w-4 rounded-full bg-white shadow transition-transform ${
                            enableAgeGroup ? "translate-x-5" : "translate-x-0"
                          }`}
                        ></span>
                        <span className="sr-only">Toggle age group results</span>
                      </button>
                      {enableAgeGroup && (
                        <button
                          type="button"
                          onClick={() => setShowAgeGroupEditor(true)}
                          className="ml-1 shrink-0 rounded-full border border-slate-200 bg-white px-2.5 py-0.5 text-[11px] font-medium text-slate-600 hover:bg-slate-50"
                        >
                          {ageGroups.length ? `${ageGroups.length} bands` : "Bands"}
                        </button>
                      )}

                      {/* Water-station splits in the public runner modal. Off by
                          default: they are typed by officers, so a late entry
                          shows as an impossible leg. */}
                      <span className="ml-3 text-xs font-semibold text-slate-700">
                        Splits
                      </span>
                      <button
                        type="button"
                        onClick={() => setShowSplits(!showSplits)}
                        className={`relative inline-flex h-5 w-10 items-center rounded-full transition-colors ${
                          showSplits ? "bg-teal-500" : "bg-slate-300"
                        }`}
                        aria-pressed={showSplits}
                      >
                        <span
                          className={`absolute top-0.5 left-0.5 h-4 w-4 rounded-full bg-white shadow transition-transform ${
                            showSplits ? "translate-x-5" : "translate-x-0"
                          }`}
                        ></span>
                        <span className="sr-only">Toggle checkpoint splits in the runner details</span>
                      </button>
                    </div>
                    <div className="flex shrink-0 items-center gap-2">
                      <span className="text-xs font-semibold text-slate-700">
                        Status
                      </span>
                      <div className="flex items-center gap-1.5">
                        {["ongoing", "completed"].map((status) => (
                          <button
                            type="button"
                            key={status}
                            onClick={() => setEventStatus(status)}
                            aria-pressed={eventStatus === status}
                            className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[11px] font-semibold capitalize leading-5 transition ${
                              eventStatus === status
                                ? status === "ongoing"
                                  ? "border-emerald-200 bg-emerald-50 text-emerald-700"
                                  : "border-slate-900 bg-slate-900 text-white"
                                : "border-slate-200 bg-white text-slate-500 hover:border-slate-300 hover:text-slate-700"
                            }`}
                          >
                            <span
                              className={`h-1.5 w-1.5 rounded-full ${
                                eventStatus === status
                                  ? status === "ongoing"
                                    ? "bg-emerald-500"
                                    : "bg-white"
                                  : "bg-slate-300"
                              }`}
                            />
                            {status}
                          </button>
                        ))}
                      </div>

                    </div>
                  </div>

                  <div className="space-y-2">
                    <FormField
                      label="Livetrack URL (Optional)"
                      name="livetrackUrl"
                      value={livetrackUrl}
                      onChange={(e) => setLivetrackUrl(e.target.value)}
                      placeholder="e.g., https://tracking.vionixasia.com/map/event_id_here"
                    />
                  </div>

                  <div className="space-y-3">
                    <div className="flex items-center justify-between gap-3">
                      <p className="text-sm font-semibold text-slate-900">
                        Quick Links
                      </p>
                      {!selectedEventId && (
                        <span className="text-[11px] text-slate-400">
                          Available after an event is saved or opened
                        </span>
                      )}
                    </div>
                    {/* Row count is items ÷ columns, so the fix is columns, not
                        spacing: ten items over three columns is four rows, over
                        five it is two. The labels are short enough that five fit
                        a desktop panel, and the grid keeps them aligned with the
                        rest of the form rather than ragged like a chip cloud. */}
                    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
                      {quickLinks.map((link) =>
                        selectedEventId ? (
                          <a
                            key={link.label}
                            href={link.href}
                            target="_blank"
                            rel="noopener noreferrer"
                            className={QUICK_CHIP}
                          >
                            {link.label}
                          </a>
                        ) : (
                          <span
                            key={link.label}
                            className={`${QUICK_CHIP} cursor-not-allowed opacity-50`}
                          >
                            {link.label}
                          </span>
                        ),
                      )}
                      <button
                        type="button"
                        onClick={handleExportExcel}
                        disabled={!selectedEventId}
                        className={`${QUICK_CHIP} disabled:cursor-not-allowed disabled:opacity-50`}
                      >
                        <FontAwesomeIcon icon={faFileCsv} className="text-slate-400" />
                        <span>Excel</span>
                      </button>
                      <button
                        type="button"
                        onClick={handleExportPodium}
                        disabled={!selectedEventId}
                        title={`Top ${PODIUM_PLACES} per gender, per category — A4 PDF`}
                        className={`${QUICK_CHIP} disabled:cursor-not-allowed disabled:opacity-50`}
                      >
                        <FontAwesomeIcon icon={faTrophy} className="text-slate-400" />
                        <span>Top {PODIUM_PLACES} PDF</span>
                      </button>
                      <button
                        type="button"
                        onClick={handleExportItra}
                        disabled={!selectedEventId}
                        title="Ranked finishers per category, in ITRA's submission layout"
                        className={`${QUICK_CHIP} disabled:cursor-not-allowed disabled:opacity-50`}
                      >
                        <FontAwesomeIcon icon={faCertificate} className="text-slate-400" />
                        <span>ITRA</span>
                      </button>
                    </div>
                  </div>
                </div>
              </Section>
            </div>
          </motion.div>

          <Section title="Data & Timing">
            {/* ═══════════════════════════════════════════════════════════════
                  CONSOLIDATED DATA MANAGEMENT PANEL
                  ─────────────────────────────────────────────────────────────
                  Unified card container with 3 tools relevant to race data
                  ingestion, presented with identical visual weight and typography:
                    1. Participant CSV format reference
                    2. MYLAPS TCP live decoder monitor
                    3. Results CSV import (offline fallback)
                  Design: flat white cards, single divider line, neutral palette
                  (gray + one accent per section), lucide-style monochrome icons.
                ═══════════════════════════════════════════════════════════════ */}
            <div className="bg-white border border-slate-200 rounded-xl divide-y divide-slate-200">
              {/* Panel 1 — Participant CSV Format Reference */}
              <div className="p-4">
                <div className="mb-2">
                  <h4 className="text-sm font-semibold text-slate-900">
                    Participant CSV Format
                  </h4>
                </div>
                <div className="overflow-x-auto rounded-xl border border-slate-200">
                  <table className="w-full min-w-[760px] table-fixed text-xs text-slate-600">
                    <colgroup>
                      <col className="w-32" />
                      <col />
                      <col className="w-32" />
                      <col />
                    </colgroup>
                    <tbody className="divide-y divide-slate-200">
                      <tr>
                        <th
                          scope="row"
                          className="bg-slate-50 px-3 py-2 text-left font-semibold text-slate-700"
                        >
                          Required
                        </th>
                        <td className="px-3 py-2">
                          <code className="rounded bg-slate-100 px-1.5 py-0.5 text-slate-800">
                            bib
                          </code>
                          ,{" "}
                          <code className="rounded bg-slate-100 px-1.5 py-0.5 text-slate-800">
                            name
                          </code>
                          ,{" "}
                          <code className="rounded bg-slate-100 px-1.5 py-0.5 text-slate-800">
                            gender
                          </code>
                        </td>
                        <th
                          scope="row"
                          className="border-l border-slate-200 bg-slate-50 px-3 py-2 text-left font-semibold text-slate-700"
                        >
                          Optional
                        </th>
                        <td className="px-3 py-2">
                          <code className="rounded bg-slate-100 px-1.5 py-0.5 text-slate-800">
                            nationality
                          </code>
                          ,{" "}
                          <code className="rounded bg-slate-100 px-1.5 py-0.5 text-slate-800">
                            age
                          </code>
                          ,{" "}
                          <code className="rounded bg-slate-100 px-1.5 py-0.5 text-slate-800">
                            dateofbirth
                          </code>
                          ,{" "}
                          <code className="rounded bg-red-50 px-1.5 py-0.5 font-semibold text-red-700">
                            chipcode (required for loop timing)
                          </code>
                          ,{" "}
                          <code className="rounded bg-slate-100 px-1.5 py-0.5 text-slate-800">
                            teamid
                          </code>
                          ,{" "}
                          <code className="rounded bg-slate-100 px-1.5 py-0.5 text-slate-800">
                            teamname
                          </code>
                        </td>
                      </tr>
                      <tr>
                        <th
                          scope="row"
                          className="bg-slate-50 px-3 py-2 text-left font-semibold text-slate-700"
                        >
                          Gender
                        </th>
                        <td className="px-3 py-2">
                          Male / M / Pria{" "}
                          <span className="mx-2 text-slate-300">|</span> Female
                          / F / Wanita
                        </td>
                        <th
                          scope="row"
                          className="border-l border-slate-200 bg-slate-50 px-3 py-2 text-left font-semibold text-slate-700"
                        >
                          Date of birth
                        </th>
                        <td className="px-3 py-2">
                          DD-MM-YYYY
                          <span className="mx-2 text-slate-300">·</span>
                          MM-DD-YYYY
                          <span className="mx-2 text-slate-300">·</span>
                          YYYY-MM-DD
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Panel 2 — MYLAPS TCP Live Monitor */}
              <div className="p-4">
                {selectedEventId && (
                  <div className={`mb-3 rounded-xl border px-3 py-3 ${raceReadiness?.dataReady && tcpClients.length > 0 ? "border-emerald-200 bg-emerald-50" : "border-red-200 bg-red-50"}`}>
                    <div className="flex items-center justify-between gap-3">
                      <div>
                        <p className="text-xs font-bold text-slate-900">
                          {raceReadiness?.dataReady && tcpClients.length > 0 ? "RACE GATE: READY" : "RACE GATE: NOT READY"}
                        </p>
                        <p className="mt-1 text-[11px] text-slate-600">
                          {readinessError
                            ? `Readiness check failed: ${readinessError}`
                            : `${raceReadiness?.checks?.participantCount ?? 0} participants · ${raceReadiness?.checks?.chipMappingCount ?? 0} chip mappings · ${tcpClients.length} T&S connections`}
                        </p>
                      </div>
                      <Button onClick={fetchRaceReadiness} variant="muted" color="slate" className="!px-3 !py-1.5 !text-xs">
                        Refresh gate
                      </Button>
                    </div>
                    {raceReadiness && !raceReadiness.dataReady && (
                      <div className="mt-2 grid gap-1 sm:grid-cols-2">
                        {raceReadiness.categories.filter((cat) => !cat.ready).map((cat) => (
                          <span key={cat.id} className="text-[10px] text-red-700">
                            {cat.name}: {cat.participants} participants, {cat.validChips} chips
                            {cat.relayTimingMode === "team_chip" ? " · mode: one chip per team" : ""}
                            {cat.relayTimingMode === "member_chips" ? " · mode: one chip per member" : ""}
                            {cat.relayErrors?.length ? ` · ${cat.relayErrors.join("; ")}` : ""}
                            {cat.missingRequiredChips?.length ? ` · missing chip: ${cat.missingRequiredChips.join(", ")}` : ""}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                )}
                {/* Where passings actually land. The decoder does not choose an
                    event — the ingest picks one from the "ongoing" events, so
                    this is the only place a mis-route is visible before a race
                    is already ruined. */}
                {/* Race-day capacity. Shown next to the routing check because
                    both are pre-race questions that the event form itself
                    cannot answer. */}
                {(flyOps || flyOpsError) && (
                  <div className="mb-3 rounded-xl border border-slate-200 bg-white px-3 py-2.5">
                    <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
                      <p className="text-xs font-semibold text-slate-800">
                        Server capacity
                      </p>
                      <Button
                        onClick={fetchFlyOps}
                        variant="muted"
                        color="slate"
                        className="shrink-0 !px-2.5 !py-1.5 !text-[11px] !font-semibold"
                      >
                        <FontAwesomeIcon icon={faRotate} />
                        Refresh
                      </Button>
                    </div>
                    {flyOpsError && (
                      <p className="mb-2 rounded-lg bg-amber-50 px-2.5 py-1.5 text-[11px] text-amber-800">
                        {flyOpsError}
                      </p>
                    )}
                    {flySetup && (
                      <div className="mb-2 rounded-lg border border-amber-200 bg-amber-50 px-2.5 py-2">
                        <p className="text-[11px] font-semibold text-amber-900">
                          Switching modes from here needs a Fly API token
                        </p>
                        <p className="mt-0.5 text-[11px] text-amber-800">
                          Run these once on your laptop, then press Refresh. The
                          specs below are what each mode would apply.
                        </p>
                        <pre className="mt-1.5 overflow-x-auto rounded bg-amber-100/70 px-2 py-1.5 text-[10px] leading-relaxed text-amber-900">
{`fly tokens create org
fly secrets set FLY_API_TOKEN="<paste token>" -a vionix-backend`}
                        </pre>
                        <p className="mt-1 text-[10px] text-amber-700">
                          Prefer no stored credential? ./race-mode.sh status —
                          same job from the terminal.
                        </p>
                      </div>
                    )}
                    <div className="flex flex-col gap-2">
                      {(flyOps || []).map((item) => (
                        <div
                          key={item.app}
                          className={`rounded-lg border px-2.5 py-2 ${
                            item.mode === "race"
                              ? "border-emerald-200 bg-emerald-50"
                              : item.mode === "mixed"
                                ? "border-amber-300 bg-amber-50"
                                : "border-slate-200 bg-slate-50"
                          }`}
                        >
                          <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                            <div className="min-w-0">
                              <div className="flex flex-wrap items-baseline gap-x-2">
                                <p className="text-xs font-semibold text-slate-800">
                                  {item.label}
                                </p>
                                <span className="text-[10px] text-slate-500">
                                  {item.app}
                                </span>
                              </div>
                              {item.error ? (
                                <p className="mt-0.5 text-[11px] text-amber-800">
                                  {item.error}
                                </p>
                              ) : (
                                <p className="mt-0.5 text-[11px] text-slate-600">
                                  <span
                                    className={`font-semibold ${
                                      item.mode === "race"
                                        ? "text-emerald-800"
                                        : item.mode === "mixed"
                                          ? "text-amber-800"
                                          : "text-slate-700"
                                    }`}
                                  >
                                    {item.mode === "race"
                                      ? "RACE"
                                      : item.mode === "mixed"
                                        ? "MIXED"
                                        : item.mode === "unknown"
                                          ? "UNKNOWN"
                                          : "STANDBY"}
                                  </span>
                                  {item.guest && (
                                    <>
                                      {" · "}
                                      {formatGuest(item.guest)}
                                    </>
                                  )}
                                  {item.state && item.state !== "unknown" && (
                                    <>
                                      {" · "}
                                      {item.state}
                                    </>
                                  )}
                                  {/* Without live data the row would say
                                      nothing at all, so show what each mode
                                      targets instead. */}
                                  {item.mode === "unknown" && item.race && (
                                    <>
                                      {" · race "}
                                      {formatGuest(item.race)}
                                      {" / standby "}
                                      {formatGuest(item.standby)}
                                    </>
                                  )}
                                </p>
                              )}
                            </div>
                            {!item.error && item.mode !== "unknown" && (
                              <div className="flex shrink-0 gap-1.5">
                                <Button
                                  onClick={() => setFlyMode(item.app, "race")}
                                  disabled={
                                    flyOpsBusy === item.app || item.mode === "race"
                                  }
                                  variant="muted"
                                  color="slate"
                                  className="!px-2.5 !py-1.5 !text-[11px] !font-semibold"
                                >
                                  Race
                                </Button>
                                <Button
                                  onClick={() => setFlyMode(item.app, "standby")}
                                  disabled={
                                    flyOpsBusy === item.app ||
                                    item.mode === "standby"
                                  }
                                  variant="muted"
                                  color="slate"
                                  className="!px-2.5 !py-1.5 !text-[11px] !font-semibold"
                                >
                                  Standby
                                </Button>
                              </div>
                            )}
                          </div>
                          {item.note && item.mode !== "race" && (
                            <p className="mt-1 text-[10px] text-slate-500">
                              {item.note}
                            </p>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                {routingStatus && (
                  <div
                    className={`mb-3 rounded-xl border px-3 py-2.5 ${
                      routingStatus.tone === "danger"
                        ? "border-red-300 bg-red-50"
                        : routingStatus.tone === "warn"
                          ? "border-amber-300 bg-amber-50"
                          : "border-emerald-200 bg-emerald-50"
                    }`}
                  >
                    <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
                      <div className="min-w-0">
                        <div className="mb-1 flex flex-wrap items-center gap-2">
                          <span
                            className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[11px] font-semibold ${
                              routingStatus.tone === "danger"
                                ? "border-red-300 bg-red-100 text-red-800"
                                : routingStatus.tone === "warn"
                                  ? "border-amber-300 bg-amber-100 text-amber-800"
                                  : "border-emerald-300 bg-emerald-100 text-emerald-800"
                            }`}
                          >
                            <span
                              className={`h-1.5 w-1.5 rounded-full ${
                                routingStatus.tone === "danger"
                                  ? "bg-red-600"
                                  : routingStatus.tone === "warn"
                                    ? "bg-amber-500"
                                    : "bg-emerald-500"
                              }`}
                            />
                            Passing routing: {routingStatus.label}
                          </span>
                          {ingestHealth?.connectedClients !== undefined && (
                            <span className="text-[11px] text-slate-500">
                              {ingestHealth.connectedClients} decoder
                              {ingestHealth.connectedClients === 1 ? "" : "s"} connected
                            </span>
                          )}
                        </div>
                        <p
                          className={`text-xs font-semibold ${
                            routingStatus.tone === "danger"
                              ? "text-red-900"
                              : routingStatus.tone === "warn"
                                ? "text-amber-900"
                                : "text-emerald-900"
                          }`}
                        >
                          {routingStatus.title}
                        </p>
                        <p
                          className={`mt-0.5 text-[11px] ${
                            routingStatus.tone === "danger"
                              ? "text-red-800"
                              : routingStatus.tone === "warn"
                                ? "text-amber-800"
                                : "text-emerald-800"
                          }`}
                        >
                          {routingStatus.detail}
                        </p>
                      </div>
                      <Button
                        onClick={refreshIngestEvent}
                        disabled={ingestRefreshing}
                        variant="muted"
                        color="slate"
                        className="shrink-0 !px-3 !py-2 !text-xs !font-semibold"
                      >
                        <FontAwesomeIcon
                          icon={faRotate}
                          className={ingestRefreshing ? "animate-spin" : ""}
                        />
                        Re-check
                      </Button>
                    </div>
                  </div>
                )}
                <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
                  <h4 className="text-sm font-semibold text-slate-900">
                    MYLAPS Connection Monitor
                  </h4>
                  <span
                    className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[11px] font-semibold ${tcpStatus.badge}`}
                  >
                    <span
                      className={`h-1.5 w-1.5 rounded-full ${tcpStatus.dot}`}
                    />
                    {tcpStatus.label}
                  </span>
                </div>
                <div className="mb-3 rounded-xl border border-slate-200 bg-slate-50 px-3 py-2.5">
                  <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                    <div className="min-w-0">
                      <div className="flex flex-wrap items-baseline gap-x-2">
                        <p className="text-xs font-semibold text-slate-800">
                          {tcpStatus.title}
                        </p>
                        <p className="text-[11px] text-slate-500">
                          {tcpStatus.description}
                        </p>
                      </div>
                    </div>

                    <Button
                      onClick={tcpFetchClients}
                      disabled={tcpLoading}
                      variant="muted"
                      color="slate"
                      className="shrink-0 !px-3 !py-2 !text-xs !font-semibold"
                    >
                      <FontAwesomeIcon
                        icon={faRotate}
                        className={tcpLoading ? "animate-spin" : ""}
                      />
                      Check connection
                    </Button>
                  </div>

                  {tcpToolsEnabled && (
                    <div className="mt-2 flex flex-wrap gap-2 border-t border-slate-200 pt-2">
                      <Button
                        onClick={() =>
                          tcpBroadcast("/tcp/getlocations", "Read locations")
                        }
                        disabled={tcpLoading}
                        variant="muted"
                        color="slate"
                        className="!px-3 !py-1.5 !text-xs !font-medium"
                      >
                        <FontAwesomeIcon icon={faLocationDot} />
                        Read locations
                      </Button>
                      <Button
                        onClick={() =>
                          tcpBroadcast("/tcp/getinfo", "Read device info")
                        }
                        disabled={tcpLoading}
                        variant="muted"
                        color="slate"
                        className="!px-3 !py-1.5 !text-xs !font-medium"
                      >
                        <FontAwesomeIcon icon={faMicrochip} />
                        Device info
                      </Button>
                      <Button
                        onClick={() =>
                          tcpBroadcast("/tcp/ping", "Ping decoder")
                        }
                        disabled={tcpLoading}
                        variant="muted"
                        color="slate"
                        className="!px-3 !py-1.5 !text-xs !font-medium"
                      >
                        <FontAwesomeIcon icon={faSatelliteDish} />
                        Ping decoder
                      </Button>
                    </div>
                  )}
                </div>

                {tcpLastAction && (
                  <div className="mb-2 text-[11px] text-slate-500">
                    Last action:{" "}
                    <span className="font-medium text-slate-600">
                      {tcpLastAction}
                    </span>
                  </div>
                )}

                {tcpClients.length > 0 && (
                  <div className="overflow-x-auto bg-white rounded-xl border border-slate-200">
                    <table className="w-full text-xs">
                      <thead className="bg-slate-100 border-b border-slate-200">
                        <tr className="text-slate-600">
                          <th className="px-3 py-2 text-left font-medium">
                            Client
                          </th>
                          <th className="px-3 py-2 text-left font-medium">
                            Last Seen
                          </th>
                          <th className="px-3 py-2 text-left font-medium">
                            Locations
                          </th>
                          <th className="px-3 py-2 text-left font-medium">
                            Devices
                          </th>
                          <th className="px-3 py-2 text-left font-medium"></th>
                        </tr>
                      </thead>
                      <tbody>
                        {tcpClients.map((c) => {
                          const expanded = !!tcpExpanded[c.key];
                          const lastSeenMs = c.lastSeen
                            ? Date.now() - new Date(c.lastSeen).getTime()
                            : 0;
                          const stale = lastSeenMs > 60 * 1000;
                          const lastSeenDate = c.lastSeen
                            ? new Date(c.lastSeen)
                            : null;
                          return (
                            <React.Fragment key={c.key}>
                              <tr className="border-b border-slate-100 hover:bg-slate-100">
                                <td className="px-3 py-2 text-slate-900">
                                  <span className="block font-medium">
                                    {c.sourceName || "MYLAPS decoder"}
                                  </span>
                                  <span className="block font-mono text-[10px] text-slate-500">
                                    {c.key}
                                  </span>
                                </td>
                                <td
                                  className={`px-3 py-2 ${stale ? "text-red-600" : "text-emerald-700"}`}
                                >
                                  <span
                                    className={`inline-block w-1.5 h-1.5 rounded-full mr-1.5 ${stale ? "bg-red-500" : "bg-emerald-500"}`}
                                  ></span>
                                  {lastSeenDate ? (
                                    <span className="inline-flex flex-col align-middle">
                                      <span>
                                        {lastSeenDate.toLocaleDateString(
                                          "en-GB",
                                          {
                                            day: "2-digit",
                                            month: "short",
                                            year: "numeric",
                                          },
                                        )}
                                      </span>
                                      <span className="text-[10px] opacity-80">
                                        {lastSeenDate.toLocaleTimeString(
                                          "en-GB",
                                        )}
                                      </span>
                                    </span>
                                  ) : (
                                    "—"
                                  )}
                                </td>
                                <td className="px-3 py-2 text-slate-600">
                                  {c.locations === null ||
                                  c.locations === undefined ? (
                                    <span className="text-slate-400">
                                      Not requested
                                    </span>
                                  ) : c.locations.length ? (
                                    c.locations.join(", ")
                                  ) : (
                                    <span className="text-amber-600">
                                      No response
                                    </span>
                                  )}
                                </td>
                                <td className="px-3 py-2 text-slate-600">
                                  {c.deviceInfo === null ||
                                  c.deviceInfo === undefined ? (
                                    <span className="text-slate-400">
                                      Not requested
                                    </span>
                                  ) : c.deviceInfo.length ? (
                                    c.deviceInfo.length
                                  ) : (
                                    <span className="text-amber-600">
                                      No response
                                    </span>
                                  )}
                                </td>
                                <td className="px-3 py-2 text-right">
                                  <button
                                    type="button"
                                    onClick={() =>
                                      setTcpExpanded((s) => ({
                                        ...s,
                                        [c.key]: !s[c.key],
                                      }))
                                    }
                                    className="text-slate-600 hover:text-slate-900 text-[11px] underline-offset-2 hover:underline"
                                  >
                                    {expanded ? "Hide details" : "View details"}
                                  </button>
                                </td>
                              </tr>
                              {expanded && (
                                <tr>
                                  <td
                                    colSpan={5}
                                    className="px-3 py-2 bg-slate-100"
                                  >
                                    <pre className="text-[11px] text-slate-600 whitespace-pre-wrap break-all font-mono">
                                      {JSON.stringify(
                                        {
                                          locations: c.locations,
                                          deviceInfo: c.deviceInfo,
                                        },
                                        null,
                                        2,
                                      )}
                                    </pre>
                                  </td>
                                </tr>
                              )}
                            </React.Fragment>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>

              {/* Panel 3 — Results Import (CSV / Excel) */}
              {selectedEventId && (
                <div className="p-4">
                  <div className="mb-3 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                    <div>
                      <h4 className="text-sm font-semibold text-slate-900">
                        Import Results (CSV / Excel)
                      </h4>
                      <p className="mt-0.5 text-[11px] text-slate-500">
                        Upload exported timing results into this event.
                      </p>
                    </div>
                    <Button
                      as="label"
                      variant="muted"
                      color="slate"
                      disabled={isUploading}
                      className="shrink-0 !px-3 !py-2 !text-xs !font-semibold"
                    >
                      <FontAwesomeIcon icon={faUpload} />
                      Choose results file
                      <input
                        type="file"
                        accept=".csv,.tsv,.txt,.xlsx,.xls"
                        className="hidden"
                        onChange={(e) => {
                          const f = e.target.files?.[0];
                          if (f) uploadResultsFile(selectedEventId, f);
                          e.target.value = "";
                        }}
                        disabled={isUploading}
                      />
                    </Button>
                  </div>

                  <div className="grid gap-px overflow-hidden rounded-xl border border-slate-200 bg-slate-200 text-xs sm:grid-cols-3">
                    <div className="bg-slate-50 px-3 py-2">
                      <span className="block text-[10px] font-semibold uppercase tracking-wide text-slate-400">
                        MYLAPS export
                      </span>
                      <code className="mt-1 block text-slate-700">
                        File → Add exporter → File
                      </code>
                    </div>
                    <div className="bg-white px-3 py-2">
                      <span className="block text-[10px] font-semibold uppercase tracking-wide text-slate-400">
                        Recognised data
                      </span>
                      <span className="mt-1 block text-slate-600">
                        BIB, chip, timing, and location
                      </span>
                    </div>
                    <div className="bg-white px-3 py-2">
                      <span className="block text-[10px] font-semibold uppercase tracking-wide text-slate-400">
                        Accepted files
                      </span>
                      <span className="mt-1 block text-slate-600">
                        CSV, TSV, TXT, XLSX, or XLS
                      </span>
                    </div>
                  </div>

                  <div>
                    {/* Scoped progress — only renders while a results import is running or just finished */}
                    {uploadContext?.scope === "results" && uploadMessage && (
                      <div
                        className={`mt-3 rounded-xl border px-3 py-2 text-xs ${
                          uploadTone === "error"
                            ? "bg-red-50 border-red-200 text-red-700"
                            : uploadTone === "success"
                              ? "bg-emerald-50 border-emerald-200 text-emerald-700"
                              : "bg-slate-50 border-slate-200 text-slate-700"
                        }`}
                      >
                        <div className="flex items-center justify-between gap-3">
                          <span className="font-medium">{uploadMessage}</span>
                          {isUploading && (
                            <span className="font-mono text-[11px] text-slate-500">
                              {uploadProgress}%
                            </span>
                          )}
                        </div>
                        {isUploading && (
                          <div className="mt-2 w-full bg-slate-200 rounded-full h-1.5 overflow-hidden">
                            <div
                              className="bg-slate-800 h-1.5 rounded-full transition-all duration-300"
                              style={{ width: `${uploadProgress}%` }}
                            />
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </Section>

          <Section title="Categories">
            <div className="flex flex-col gap-4">
              {categories.map((cat, catIndex) => (
                <div
                  key={catIndex}
                  className="border border-slate-200 rounded-xl p-4 bg-white"
                >
                  <div className="flex flex-col gap-2">
                    <div className="grid grid-cols-1 md:grid-cols-[1fr_2fr_2fr_2fr_auto_auto] gap-4 items-start md:items-end w-full">
                      <div className="flex gap-2 items-start md:items-end w-full">
                        <FormField
                          label="Category ID"
                          name={`category-${catIndex}-id`}
                          value={cat.id}
                          onChange={(e) => {
                            const newCats = [...categories];
                            newCats[catIndex].id = e.target.value;
                            setCategories(newCats);
                          }}
                          placeholder="e.g., 7k"
                          wrapperClassName="w-full"
                        />
                        <Button
                          onClick={() => handleRemoveCategory(catIndex)}
                          variant="muted"
                          color="slate"
                          className="md:hidden h-10 w-10 p-0 flex items-center justify-center flex-shrink-0 self-start md:self-end"
                          title="Remove Category"
                        >
                          <FontAwesomeIcon icon={faTrash} />
                        </Button>
                      </div>
                      <FormField
                        label="Category Name"
                        name={`category-${catIndex}-name`}
                        value={cat.name}
                        onChange={(e) => {
                          const newCats = [...categories];
                          const newVal = e.target.value;
                          const oldName = newCats[catIndex].name || "";
                          const currentId = newCats[catIndex].id || "";

                          const format = (s) => s.trim().replace(/\s+/g, "_");

                          newCats[catIndex].name = newVal;

                          if (!currentId || currentId === format(oldName)) {
                            newCats[catIndex].id = format(newVal);
                          }

                          setCategories(newCats);
                        }}
                        placeholder="e.g., 7K Race"
                        wrapperClassName="w-full"
                      />
                      {/* Hidden for buzzer timing: each athlete's clock starts
                          on their own press, so a category start and cut-off
                          describe nothing. Showing them would invite invented
                          values into the record. */}
                      {!cat.isBuzzerTimed && (
                        <>
                          <FormField
                            label="Start Time"
                            name={`category-${catIndex}-startTime`}
                            type="datetime-local"
                            value={cat.startTime}
                            onChange={(e) => {
                              const newCats = [...categories];
                              newCats[catIndex].startTime = e.target.value;
                              setCategories(newCats);
                            }}
                            wrapperClassName="w-full"
                          />
                          <FormField
                            label="Cut Off Time"
                            name={`category-${catIndex}-cutOffTime`}
                            type="datetime-local"
                            value={cat.cutOffTime}
                            onChange={(e) => {
                              const newCats = [...categories];
                              newCats[catIndex].cutOffTime = e.target.value;
                              setCategories(newCats);
                            }}
                            wrapperClassName="w-full"
                          />
                        </>
                      )}

                      <Button
                        as="label"
                        variant="muted"
                        color="slate"
                        className="cursor-pointer self-start md:self-end"
                      >
                        <FontAwesomeIcon icon={faPlus} className="text-xs" />
                        Upload CSV
                        <input
                          type="file"
                          accept=".csv"
                          className="hidden"
                          disabled={isUploading || !cat.id}
                          onChange={(e) => {
                            if (e.target.files[0]) {
                              uploadCategoryParticipants(
                                selectedEventId,
                                e.target.files[0],
                                cat.id,
                                cat.name || cat.id,
                              );
                              e.target.value = null;
                            }
                          }}
                        />
                      </Button>

                      <Button
                        onClick={() => handleRemoveCategory(catIndex)}
                        variant="muted"
                        color="slate"
                        className="hidden md:flex h-10 w-10 p-0 items-center justify-center self-start md:self-end"
                        title="Remove Category"
                      >
                        <FontAwesomeIcon icon={faTrash} />
                      </Button>
                    </div>

                    {/* Scoped participant-upload progress — only renders for the category that launched the upload */}
                    {uploadContext?.scope === "participant" &&
                      uploadContext.categoryId === cat.id &&
                      uploadMessage && (
                        <div
                          className={`mt-3 rounded-xl border px-3 py-2 text-xs ${
                            uploadTone === "error"
                              ? "bg-red-50 border-red-200 text-red-700"
                              : uploadTone === "success"
                                ? "bg-emerald-50 border-emerald-200 text-emerald-700"
                                : "bg-slate-50 border-slate-200 text-slate-700"
                          }`}
                        >
                          <div className="flex items-center justify-between gap-3">
                            <span className="font-medium">{uploadMessage}</span>
                            {isUploading && (
                              <span className="font-mono text-[11px] text-slate-500">
                                {uploadProgress}%
                              </span>
                            )}
                          </div>
                          {isUploading && (
                            <div className="mt-2 w-full bg-slate-200 rounded-full h-1.5 overflow-hidden">
                              <div
                                className="bg-slate-800 h-1.5 rounded-full transition-all duration-300"
                                style={{ width: `${uploadProgress}%` }}
                              />
                            </div>
                          )}
                        </div>
                      )}

                    {/* Looping Event Configuration */}
                    <div className="mt-3 rounded-xl border border-slate-200 bg-slate-50 px-3 py-2.5">
                      <div className="flex flex-wrap items-center gap-4">
                        {/* Looping Toggle */}
                        <div className="flex items-center gap-2">
                          <span className="text-xs font-semibold text-slate-700">
                            Looping Event
                          </span>
                          <div className="relative">
                            <input
                              type="checkbox"
                              checked={cat.isLooping || false}
                              onChange={(e) => {
                                const newCats = [...categories];
                                newCats[catIndex].isLooping = e.target.checked;
                                // The two timing models are mutually exclusive:
                                // a lap-counted race cannot also be a single
                                // buzzer run.
                                if (e.target.checked) {
                                  newCats[catIndex].useIndividualStart = false;
                                  newCats[catIndex].isBuzzerTimed = false;
                                }
                                setCategories(newCats);
                              }}
                              className="sr-only"
                            />
                            <label
                              onClick={() => {
                                const newCats = [...categories];
                                const newVal = !newCats[catIndex].isLooping;
                                newCats[catIndex].isLooping = newVal;
                                if (newVal) {
                                  newCats[catIndex].useIndividualStart = false;
                                  newCats[catIndex].isBuzzerTimed = false;
                                }
                                setCategories(newCats);
                              }}
                              className={`block w-10 h-5 rounded-full transition-colors cursor-pointer ${cat.isLooping ? "bg-teal-500" : "bg-slate-300"}`}
                            ></label>
                            <div
                              onClick={() => {
                                const newCats = [...categories];
                                const newVal = !newCats[catIndex].isLooping;
                                newCats[catIndex].isLooping = newVal;
                                if (newVal) {
                                  newCats[catIndex].useIndividualStart = false;
                                  newCats[catIndex].isBuzzerTimed = false;
                                }
                                setCategories(newCats);
                              }}
                              className={`absolute left-0.5 top-0.5 w-4 h-4 rounded-full bg-white transition-transform cursor-pointer ${cat.isLooping ? "translate-x-5" : "translate-x-0"}`}
                            ></div>
                          </div>
                        </div>

                        {/* Buzzer timing — ninja/obstacle courses and speed
                              climbing. The athlete's own buzzer press starts and
                              stops the clock, so the category has no mass start,
                              no cut-off and no mats: the operator types the
                              elapsed time straight in. Mutually exclusive with
                              looping, which is lap-counted by definition. */}
                        {!cat.isLooping && (
                          <div className="flex items-center gap-2">
                            <span
                              className="text-xs font-semibold text-slate-700"
                              title="Ninja/obstacle or speed climbing: operators type each athlete's elapsed time from the buzzer clock. No start time, cut-off or checkpoints are used."
                            >
                              Buzzer Timing
                            </span>
                            <div className="relative">
                              <input
                                type="checkbox"
                                checked={cat.isBuzzerTimed || false}
                                onChange={(e) => {
                                  const newCats = [...categories];
                                  newCats[catIndex].isBuzzerTimed = e.target.checked;
                                  if (e.target.checked) {
                                    newCats[catIndex].useIndividualStart = false;
                                  }
                                  setCategories(newCats);
                                }}
                                className="sr-only"
                              />
                              <label
                                onClick={() => {
                                  const newCats = [...categories];
                                  const newVal = !newCats[catIndex].isBuzzerTimed;
                                  newCats[catIndex].isBuzzerTimed = newVal;
                                  if (newVal) newCats[catIndex].useIndividualStart = false;
                                  setCategories(newCats);
                                }}
                                className={`block w-10 h-5 rounded-full transition-colors cursor-pointer ${cat.isBuzzerTimed ? "bg-teal-500" : "bg-slate-300"}`}
                              ></label>
                              <div
                                onClick={() => {
                                  const newCats = [...categories];
                                  const newVal = !newCats[catIndex].isBuzzerTimed;
                                  newCats[catIndex].isBuzzerTimed = newVal;
                                  if (newVal) newCats[catIndex].useIndividualStart = false;
                                  setCategories(newCats);
                                }}
                                className={`absolute left-0.5 top-0.5 w-4 h-4 rounded-full bg-white transition-transform cursor-pointer ${cat.isBuzzerTimed ? "translate-x-5" : "translate-x-0"}`}
                              ></div>
                            </div>
                          </div>
                        )}

                        {/* Hundredths — for short hand-timed formats (pool
                              races, sprints) where places are split by
                              hundredths. Time entry becomes mm:ss.cc and every
                              results view keeps two decimals for this category. */}
                        {!cat.isLooping && cat.isBuzzerTimed && (
                          <div className="flex items-center gap-2">
                            <span
                              className="text-xs font-semibold text-slate-700"
                              title="Enter times as mm:ss.cc and show them to the hundredth on every results page."
                            >
                              Hundredths
                            </span>
                            <button
                              type="button"
                              onClick={() => {
                                const newCats = [...categories];
                                newCats[catIndex].showHundredths = !newCats[catIndex].showHundredths;
                                setCategories(newCats);
                              }}
                              className={`relative inline-flex h-5 w-10 items-center rounded-full transition-colors ${
                                cat.showHundredths ? "bg-teal-500" : "bg-slate-300"
                              }`}
                              aria-pressed={!!cat.showHundredths}
                            >
                              <span
                                className={`absolute top-0.5 left-0.5 h-4 w-4 rounded-full bg-white shadow transition-transform ${
                                  cat.showHundredths ? "translate-x-5" : "translate-x-0"
                                }`}
                              ></span>
                              <span className="sr-only">Toggle hundredths</span>
                            </button>
                          </div>
                        )}

                        {/* Relay — only meaningful for looping events: two or
                              more runners share a teamId and are scored as one
                              team entry. Hidden otherwise so non-relay events
                              never see (or set) the flag. */}
                        {cat.isLooping && (
                          <div className="flex items-center gap-2">
                            <span
                              className="text-xs font-semibold text-slate-700"
                              title="Two or more runners share a teamId in the participants CSV and are ranked as a single team row."
                            >
                              Relay (Team)
                            </span>
                            <div className="relative">
                              <input
                                type="checkbox"
                                checked={cat.isRelay || false}
                                onChange={(e) => {
                                  const newCats = [...categories];
                                  newCats[catIndex].isRelay = e.target.checked;
                                  newCats[catIndex].relayTimingMode = e.target.checked
                                    ? "team_chip"
                                    : undefined;
                                  setCategories(newCats);
                                }}
                                className="sr-only"
                              />
                              <div
                                onClick={() => {
                                  const newCats = [...categories];
                                  const enabled = !newCats[catIndex].isRelay;
                                  newCats[catIndex].isRelay = enabled;
                                  newCats[catIndex].relayTimingMode = enabled
                                    ? "team_chip"
                                    : undefined;
                                  setCategories(newCats);
                                }}
                                className={`block w-10 h-5 rounded-full transition-colors cursor-pointer ${cat.isRelay ? "bg-teal-500" : "bg-slate-300"}`}
                              ></div>
                              <div
                                onClick={() => {
                                  const newCats = [...categories];
                                  const enabled = !newCats[catIndex].isRelay;
                                  newCats[catIndex].isRelay = enabled;
                                  newCats[catIndex].relayTimingMode = enabled
                                    ? "team_chip"
                                    : undefined;
                                  setCategories(newCats);
                                }}
                                className={`absolute left-0.5 top-0.5 w-4 h-4 rounded-full bg-white transition-transform cursor-pointer ${cat.isRelay ? "translate-x-5" : "translate-x-0"}`}
                              ></div>
                            </div>
                            {cat.isRelay && (
                              <div className="flex items-center gap-2">
                                <span className="text-xs font-medium text-slate-600">
                                  Chip mode
                                </span>
                                <select
                                  value={cat.relayTimingMode || "team_chip"}
                                  onChange={(event) => {
                                    const newCats = [...categories];
                                    newCats[catIndex].relayTimingMode = event.target.value;
                                    setCategories(newCats);
                                  }}
                                  className="h-8 px-2 border border-slate-200 rounded-xl bg-white text-xs font-semibold text-slate-700 focus:outline-none focus:border-slate-400 focus:ring-2 focus:ring-slate-200"
                                  aria-label="Relay chip configuration"
                                >
                                  <option value="team_chip">One chip per team</option>
                                  <option value="member_chips">One chip per member</option>
                                </select>
                              </div>
                            )}
                          </div>
                        )}

                        {/* Min Finish Time Guard */}
                        <div className="flex items-center gap-2">
                          <label
                            className="text-xs font-medium text-slate-600"
                            title={cat.isLooping
                              ? "Minimum minutes between two accepted mat crossings. Prevents a runner turning back at the mat from adding a false loop."
                              : "Minimum minutes from START crossing to accept a FINISH crossing. Prevents false finish from runner bouncing back. Set to 0 to disable."}
                          >
                            {cat.isLooping ? "Loop Guard" : "Min Finish"}
                          </label>
                          <input
                            type="text"
                            inputMode="numeric"
                            value={cat.minFinishMinutes || 0}
                            onChange={(e) => {
                              const newCats = [...categories];
                              const val = parseInt(
                                e.target.value.replace(/\D/g, ""),
                                10,
                              );
                              newCats[catIndex].minFinishMinutes = isNaN(val)
                                ? 0
                                : val;
                              setCategories(newCats);
                            }}
                            className="w-16 h-8 px-2 text-center border border-slate-200 rounded-xl bg-white text-sm focus:outline-none focus:border-slate-400 focus:ring-2 focus:ring-slate-200"
                          />
                          <span className="text-xs text-slate-500">min</span>
                        </div>

                        {/* Start-mat window — only meaningful for a combined
                            Start/Finish mat that sits away from the start line.
                            Blank/0 falls back to Min Finish. */}
                        {!cat.isLooping && (
                          <div className="flex items-center gap-2">
                            <label
                              className="text-xs font-medium text-slate-600"
                              title="How long after flag-off a FIRST mat crossing still counts as a START. Use when the Start/Finish mat is far from the start line (e.g. beach start, mat higher up): set it above the SLOWEST runner's time to reach the mat. Leave 0 to fall back to Min Finish."
                            >
                              Start Mat Window
                            </label>
                            <input
                              type="text"
                              inputMode="numeric"
                              value={cat.startMatOffsetMinutes || 0}
                              onChange={(e) => {
                                const newCats = [...categories];
                                const val = parseInt(
                                  e.target.value.replace(/\D/g, ""),
                                  10,
                                );
                                newCats[catIndex].startMatOffsetMinutes = isNaN(val)
                                  ? 0
                                  : val;
                                setCategories(newCats);
                              }}
                              className="w-16 h-8 px-2 text-center border border-slate-200 rounded-xl bg-white text-sm focus:outline-none focus:border-slate-400 focus:ring-2 focus:ring-slate-200"
                            />
                            <span className="text-xs text-slate-500">min</span>
                          </div>
                        )}

                        {/* Finish target — looping distance categories only.
                            A timed category ends at its cut-off; capping it
                            would stop a runner's race early. */}
                        {cat.isLooping && (
                          <div className="flex items-center gap-2">
                            <label
                              className="text-xs font-medium text-slate-600"
                              title="Jumlah putaran yang setara jarak resmi kategori. Setelah pelari mencapai angka ini, lintasan berikutnya diabaikan — mereka sudah finish. Kosongkan (0) untuk kategori berwaktu seperti 24H."
                            >
                              Finish Loops
                            </label>
                            <input
                              type="text"
                              inputMode="numeric"
                              value={cat.targetLoops || 0}
                              onChange={(e) => {
                                const newCats = [...categories];
                                const val = parseInt(
                                  e.target.value.replace(/\D/g, ""),
                                  10,
                                );
                                newCats[catIndex].targetLoops = isNaN(val) ? 0 : val;
                                setCategories(newCats);
                              }}
                              className="w-16 h-8 px-2 text-center border border-slate-200 rounded-xl bg-white text-sm focus:outline-none focus:border-slate-400 focus:ring-2 focus:ring-slate-200"
                            />
                            <span className="text-xs text-slate-500">
                              {cat.targetLoops > 0 ? "loop" : "loop (0 = tanpa batas)"}
                            </span>
                          </div>
                        )}

                        {/* Lap distance. Categories can run different lanes of
                            the same track — Indonesia Ultra timed 6H/12H at 423m
                            and 24H at 407.66m — so this belongs to the category,
                            not the event. Used with each runner's extra metres
                            to publish a total distance. */}
                        {cat.isLooping && (
                          <div className="flex items-center gap-2">
                            <label
                              className="text-xs font-medium text-slate-600"
                              title="Panjang satu putaran di lane yang dipakai kategori ini, dalam meter. Total jarak = (loop x panjang) + sisa meter."
                            >
                              Lap Distance
                            </label>
                            <input
                              type="text"
                              inputMode="decimal"
                              value={cat.lapDistanceMeters ?? ""}
                              placeholder="423"
                              onChange={(e) => {
                                const newCats = [...categories];
                                // Comma is the decimal separator on an Indonesian
                                // keyboard; 407,66 must not become 40766.
                                const raw = e.target.value.replace(",", ".").replace(/[^\d.]/g, "");
                                newCats[catIndex].lapDistanceMeters = raw;
                                setCategories(newCats);
                              }}
                              className="w-20 h-8 px-2 text-center border border-slate-200 rounded-xl bg-white text-sm focus:outline-none focus:border-slate-400 focus:ring-2 focus:ring-slate-200"
                            />
                            <span className="text-xs text-slate-500">meter / loop</span>
                          </div>
                        )}

                        {/* Individual Start (Vertical / Time-Trial) */}
                        {!cat.isLooping && <div className="flex items-center gap-2">
                          <span
                            className="text-xs font-semibold text-slate-700"
                            title="Individual Start (Vertical / Time-Trial): each runner has their own start time recorded at the start mat. Ranking uses netTime (not gunTime). The pre-start guard is disabled."
                          >
                            Individual Start (Vertical)
                          </span>
                          <div className="relative">
                            <input
                              type="checkbox"
                              checked={cat.useIndividualStart || false}
                              onChange={(e) => {
                                const newCats = [...categories];
                                newCats[catIndex].useIndividualStart =
                                  e.target.checked;
                                setCategories(newCats);
                              }}
                              className="sr-only"
                            />
                            <label
                              onClick={() => {
                                const newCats = [...categories];
                                newCats[catIndex].useIndividualStart =
                                  !newCats[catIndex].useIndividualStart;
                                setCategories(newCats);
                              }}
                              className={`block w-10 h-5 rounded-full transition-colors cursor-pointer ${cat.useIndividualStart ? "bg-teal-500" : "bg-slate-300"}`}
                            ></label>
                            <div
                              onClick={() => {
                                const newCats = [...categories];
                                newCats[catIndex].useIndividualStart =
                                  !newCats[catIndex].useIndividualStart;
                                setCategories(newCats);
                              }}
                              className={`absolute left-0.5 top-0.5 w-4 h-4 rounded-full bg-white transition-transform cursor-pointer ${cat.useIndividualStart ? "translate-x-5" : "translate-x-0"}`}
                            ></div>
                          </div>
                        </div>}

                        {cat.isLooping && (
                          <div className="basis-full flex flex-wrap items-center gap-x-2 gap-y-1 text-[11px] text-slate-500">
                            {cat.isRelay && (
                              <span>
                                {(cat.relayTimingMode || "team_chip") === "member_chips"
                                  ? "Unique chip per member; all accepted crossings count for the team."
                                  : "One shared timing chip counts for the team."}
                              </span>
                            )}
                            {cat.isRelay && <span aria-hidden="true">•</span>}
                            <span>First crossing: baseline</span>
                            <span aria-hidden="true">•</span>
                            <span>Next accepted crossing: Loop 1</span>
                            <span aria-hidden="true">•</span>
                            <span>Finish time updates on every accepted loop</span>
                          </div>
                        )}
                      </div>
                    </div>

                    {cat.isLooping && cat.isRelay && cat.relayTimingMode === "member_chips" && selectedEventId && (
                      <div className="mt-2 rounded-xl border border-slate-200 bg-white px-3 py-3">
                        <div className="flex items-center justify-between gap-3">
                          <div>
                            <p className="text-xs font-bold text-slate-800">Relay Control</p>
                            <p className="mt-0.5 text-[11px] text-slate-500">MYLAPS counting stays automatic. Use this control only when the runner changes.</p>
                          </div>
                          <Button
                            onClick={() => fetchRelayLocks(cat.id)}
                            variant="muted"
                            color="slate"
                            className="!px-3 !py-1.5 !text-xs"
                          >
                            <FontAwesomeIcon icon={faRotate} />
                            Refresh
                          </Button>
                        </div>

                        {relayLocks[cat.id]?.loading ? (
                          <p className="mt-3 text-xs text-slate-500">Loading relay teams…</p>
                        ) : relayLocks[cat.id]?.error ? (
                          <p className="mt-3 text-xs text-red-600">{relayLocks[cat.id].error}</p>
                        ) : (
                          <div className="mt-3 grid gap-2 lg:grid-cols-3">
                            {(relayLocks[cat.id]?.teams || []).map((team) => {
                              const selectedBib = relayLockDrafts[cat.id]?.[team.teamId] || team.activeRunnerBib;
                              const unchanged = selectedBib === team.activeRunnerBib;
                              const busyKey = `${cat.id}:${team.teamId}`;
                              return (
                                <div key={team.teamId} className="rounded-lg border border-slate-200 bg-slate-50 p-2.5">
                                  <div className="flex items-center justify-between gap-2">
                                    <p className="truncate text-xs font-bold text-slate-800" title={team.teamName}>{team.teamName}</p>
                                    <span className="whitespace-nowrap rounded-full bg-emerald-100 px-2 py-0.5 text-[10px] font-semibold text-emerald-700">
                                      Active · {team.activeRunnerBib}
                                    </span>
                                  </div>
                                  <div className="mt-2 rounded-lg border border-emerald-200 bg-emerald-50 px-2.5 py-2">
                                    <p className="text-[10px] font-semibold uppercase tracking-wide text-emerald-700">Current runner</p>
                                    <p className="mt-0.5 truncate text-xs font-bold text-slate-900">
                                      {team.activeRunnerBib} · {team.activeRunnerName}
                                    </p>
                                    <p className="mt-0.5 text-[10px] text-slate-500">
                                      Active since {team.activeSince ? new Date(team.activeSince).toLocaleString("en-GB", { hour12: false }) : "not initialized"}
                                    </p>
                                  </div>
                                  <p className="mt-2 text-[10px] font-semibold uppercase tracking-wide text-slate-500">Next runner</p>
                                  <div className="mt-2 flex items-center gap-2">
                                    <select
                                      value={selectedBib}
                                      onChange={(event) => setRelayLockDrafts((current) => ({
                                        ...current,
                                        [cat.id]: { ...(current[cat.id] || {}), [team.teamId]: event.target.value },
                                      }))}
                                      className="min-w-0 flex-1 h-8 px-2 border border-slate-200 rounded-lg bg-white text-xs text-slate-700 focus:outline-none focus:border-slate-400 focus:ring-2 focus:ring-slate-200"
                                      aria-label={`Active runner for ${team.teamName}`}
                                    >
                                      {team.members.map((member) => (
                                        <option key={member.bib} value={member.bib}>
                                          {member.bib} · {member.name}
                                        </option>
                                      ))}
                                    </select>
                                    <Button
                                      onClick={() => requestRelayRunnerActivation(cat.id, team.teamId)}
                                      disabled={unchanged || relayLockBusy === busyKey}
                                      variant="muted"
                                      color="slate"
                                      className="!px-3 !py-1.5 !text-xs"
                                    >
                                      {relayLockBusy === busyKey ? "Switching…" : "Activate next"}
                                    </Button>
                                  </div>
                                  {(team.runnerWindows || []).filter((window) => window.activeUntil).length > 0 && (
                                    <details className="mt-2 text-[10px] text-slate-500">
                                      <summary className="cursor-pointer font-semibold text-slate-600">Switch history</summary>
                                      <div className="mt-1 space-y-1">
                                        {team.runnerWindows.filter((window) => window.activeUntil).slice().reverse().slice(0, 5).map((window, index) => (
                                          <p key={`${window.bib}-${window.activeFrom}-${index}`}>
                                            BIB {window.bib} · {new Date(window.activeFrom).toLocaleTimeString("en-GB", { hour12: false })}–{new Date(window.activeUntil).toLocaleTimeString("en-GB", { hour12: false })}
                                          </p>
                                        ))}
                                      </div>
                                    </details>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    )}
                    {/* A buzzer-timed category has no mats: the athlete's own
                        press starts and stops the clock. Hiding the whole block
                        keeps operators from inventing a checkpoint that nothing
                        will ever read. */}
                    {!cat.isBuzzerTimed && (
                    <div className="mt-2">
                      <p className="text-slate-700 mb-2">
                        Checkpoints for{" "}
                        <span className="font-semibold text-slate-900">
                          {cat.name || `Category ${catIndex + 1}`}
                        </span>
                      </p>
                      <div className="flex flex-col gap-4">
                        {cat.checkpoints.map((cp, cpIndex) => (
                          <div
                            key={cpIndex}
                            className="flex flex-col md:flex-row gap-4 items-start md:items-end border-b md:border-b-0 border-slate-100 pb-4 md:pb-0 last:border-0 last:pb-0"
                          >
                            <div className="flex gap-2 items-start md:items-end flex-1">
                              <FormField
                                label="Checkpoint ID"
                                name={`category-${catIndex}-checkpoint-${cpIndex}-id`}
                                value={cp.id}
                                onChange={(e) =>
                                  handleCheckpointIdChange(
                                    catIndex,
                                    cpIndex,
                                    e.target.value,
                                  )
                                }
                                placeholder={`cp${cpIndex + 1}`}
                                wrapperClassName="w-full"
                              />
                              <Button
                                onClick={() =>
                                  handleRemoveCheckpoint(catIndex, cpIndex)
                                }
                                variant="muted"
                                color="slate"
                                className="md:hidden h-10 w-10 p-0 flex items-center justify-center flex-shrink-0 self-start md:self-end"
                                title="Remove Checkpoint"
                              >
                                <FontAwesomeIcon icon={faTrash} />
                              </Button>
                            </div>

                            <FormField
                              label="Checkpoint Name"
                              name={`category-${catIndex}-checkpoint-${cpIndex}-name`}
                              value={cp.name}
                              onChange={(e) => {
                                const newCats = [...categories];
                                const newVal = e.target.value;
                                const oldName =
                                  newCats[catIndex].checkpoints[cpIndex].name ||
                                  "";
                                const currentLocationId =
                                  newCats[catIndex].checkpoints[cpIndex]
                                    .locationId || "";

                                const format = (s) =>
                                  s.toLowerCase().trim().replace(/\s+/g, "_");

                                newCats[catIndex].checkpoints[cpIndex].name =
                                  newVal;

                                if (
                                  !currentLocationId ||
                                  currentLocationId === format(oldName)
                                ) {
                                  newCats[catIndex].checkpoints[
                                    cpIndex
                                  ].locationId = format(newVal);
                                }

                                setCategories(newCats);
                              }}
                              placeholder="Checkpoint Name"
                              wrapperClassName="flex-1"
                            />

                            <FormField
                              label="Location ID"
                              name={`category-${catIndex}-checkpoint-${cpIndex}-location`}
                              value={cp.locationId}
                              onChange={(e) =>
                                handleCheckpointLocationIdChange(
                                  catIndex,
                                  cpIndex,
                                  e.target.value,
                                )
                              }
                              placeholder="Location ID"
                              wrapperClassName="flex-1"
                            />

                            <FormField
                              label="Type"
                              name={`category-${catIndex}-checkpoint-${cpIndex}-type`}
                              type="select"
                              value={cp.type || "cp"}
                              onChange={(e) => {
                                const newCats = [...categories];
                                newCats[catIndex].checkpoints[cpIndex].type =
                                  e.target.value;
                                setCategories(newCats);
                              }}
                              options={[
                                { value: "cp", label: "Split / CP" },
                                { value: "start", label: "Start Mat" },
                                { value: "finish", label: "Finish Mat" },
                                {
                                  value: "startfinish",
                                  label: "Start/Finish Mat",
                                },
                                {
                                  value: "postfinish",
                                  label: "Post-Finish Gate",
                                },
                              ]}
                              defaultOptionLabel={null}
                              wrapperClassName="flex-1 md:max-w-[140px]"
                            />

                            <Button
                              onClick={() =>
                                handleRemoveCheckpoint(catIndex, cpIndex)
                              }
                              variant="muted"
                              color="slate"
                              title="Remove Checkpoint"
                              className="hidden md:flex h-10 w-10 p-0 items-center justify-center self-start md:self-end"
                            >
                              <FontAwesomeIcon icon={faTrash} />
                            </Button>
                          </div>
                        ))}
                      </div>
                      <Button
                        onClick={() => handleAddCheckpoint(catIndex)}
                        variant="muted"
                        color="slate"
                        className="mt-3 w-full md:w-auto"
                      >
                        <FontAwesomeIcon icon={faPlus} /> Add checkpoint
                      </Button>
                    </div>
                    )}
                  </div>
                </div>
              ))}
              <div className="mt-4 flex flex-col gap-3 md:flex-row">
                <Button
                  onClick={handleAddCategory}
                  variant="muted"
                  color="slate"
                  fullWidth
                  className="flex-1"
                >
                  <FontAwesomeIcon icon={faPlus} /> Add category
                </Button>
                <Button
                  onClick={handleSaveEvent}
                  variant="muted"
                  color="slate"
                  fullWidth
                  className="flex-1"
                  disabled={isSaving.current || isUploading}
                >
                  <FontAwesomeIcon icon={faSave} />
                  <span>
                    {selectedEventId ? "Update event" : "Save event"}
                  </span>
                </Button>
              </div>

              {message && (
                <div
                  className={`mt-3 rounded-xl border px-3 py-2 text-xs font-medium ${
                    /error|failed|invalid|duplicate|cannot|must/i.test(message)
                      ? "bg-red-50 border-red-200 text-red-700"
                      : /success|saved|updated|created|loaded|deactivated|active/i.test(
                            message,
                          )
                        ? "bg-emerald-50 border-emerald-200 text-emerald-700"
                        : "bg-slate-50 border-slate-200 text-slate-700"
                  }`}
                >
                  {message}
                </div>
              )}
            </div>
          </Section>
        </div>
      </motion.div>

      {/* Age band editor. Bands are inclusive ranges; the last one is left
          open-ended so "50+" needs no upper number. A runner whose age falls
          outside every band simply has no age group, which is how younger
          entrants are excluded from a masters field. */}
      {showAgeGroupEditor && ReactDOM.createPortal(
        <div
          className="fixed inset-0 z-[120] flex items-center justify-center p-4"
          role="dialog"
          aria-modal="true"
          aria-labelledby="age-band-title"
          onClick={() => setShowAgeGroupEditor(false)}
        >
          <div className="absolute inset-0 bg-slate-950/45 backdrop-blur-sm" />
          <div
            className="relative flex max-h-[85vh] w-full max-w-lg flex-col overflow-hidden rounded-card border border-slate-200 bg-white shadow-2xl"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="border-b border-slate-200 px-5 py-4">
              <h3 id="age-band-title" className="text-base font-bold text-slate-900">
                Age groups
              </h3>
              <p className="mt-0.5 text-xs text-slate-500">
                Leave empty to use the default masters bands (35-39 up to 65+).
              </p>
            </div>

            <div className="flex-1 overflow-y-auto px-5 py-4">
              {ageGroups.length === 0 ? (
                <p className="rounded-lg bg-slate-50 px-3 py-4 text-center text-xs text-slate-500">
                  No custom bands — the default masters set is in use.
                </p>
              ) : (
                <div className="space-y-2">
                  {/* Column headings: the three inputs are otherwise unlabelled
                      (their own labels are screen-reader only) and the last one
                      reads as an anonymous box next to the delete button. */}
                  <div className="grid grid-cols-[5rem_5rem_1fr_2.25rem] items-center gap-2 px-1 text-[11px] font-semibold uppercase tracking-wider text-slate-500">
                    <span>From</span>
                    <span>To</span>
                    <span>Label on results</span>
                    <span />
                  </div>
                  {ageGroups.map((band, index) => (
                    <div
                      key={index}
                      className="grid grid-cols-[5rem_5rem_1fr_2.25rem] items-center gap-2"
                    >
                      <InputField
                        label="From"
                        labelClassName="sr-only"
                        name={`band-min-${index}`}
                        type="number"
                        value={band.min ?? ""}
                        placeholder="16"
                        onChange={(e) => {
                          const next = [...ageGroups];
                          const min = e.target.value === "" ? "" : Number(e.target.value);
                          next[index] = { ...band, min, label: _autoLabel(min, band.max, band.label) };
                          setAgeGroups(next);
                        }}
                        wrapperClassName=""
                      />
                      <InputField
                        label="To"
                        labelClassName="sr-only"
                        name={`band-max-${index}`}
                        type="number"
                        value={band.max ?? ""}
                        placeholder="open"
                        onChange={(e) => {
                          const next = [...ageGroups];
                          const max = e.target.value === "" ? null : Number(e.target.value);
                          next[index] = { ...band, max, label: _autoLabel(band.min, max, band.label) };
                          setAgeGroups(next);
                        }}
                        wrapperClassName=""
                      />
                      <InputField
                        label="Label"
                        labelClassName="sr-only"
                        name={`band-label-${index}`}
                        value={band.label || ""}
                        placeholder="16-24"
                        onChange={(e) => {
                          const next = [...ageGroups];
                          next[index] = { ...band, label: e.target.value };
                          setAgeGroups(next);
                        }}
                        wrapperClassName=""
                      />
                      <button
                        type="button"
                        onClick={() => setAgeGroups(ageGroups.filter((_, i) => i !== index))}
                        className="shrink-0 rounded-lg border border-slate-200 bg-white p-2 text-slate-500 hover:bg-slate-50 hover:text-slate-900"
                        title="Remove band"
                      >
                        <FontAwesomeIcon icon={faTrash} />
                      </button>
                    </div>
                  ))}
                </div>
              )}

              <div className="mt-3 flex flex-wrap gap-2">
                <Button
                  onClick={() => {
                    const last = ageGroups[ageGroups.length - 1];
                    const nextMin = last && Number.isFinite(Number(last.max)) ? Number(last.max) + 1 : "";
                    setAgeGroups([...ageGroups, { min: nextMin, max: null, label: "" }]);
                  }}
                  variant="ghost"
                  color="cyan"
                >
                  Add band
                </Button>
                {ageGroups.length === 0 && (
                  <Button
                    onClick={() =>
                      setAgeGroups([
                        { min: 16, max: 24, label: "16-24" },
                        { min: 25, max: 29, label: "25-29" },
                        { min: 30, max: 34, label: "30-34" },
                        { min: 35, max: 39, label: "35-39" },
                        { min: 40, max: 44, label: "40-44" },
                        { min: 45, max: 49, label: "45-49" },
                        { min: 50, max: null, label: "50+" },
                      ])
                    }
                    variant="ghost"
                    color="cyan"
                  >
                    Use 16-24 … 50+
                  </Button>
                )}
              </div>
            </div>

            <div className="flex justify-end gap-2 border-t border-slate-200 bg-slate-50 px-5 py-3">
              <Button onClick={() => setShowAgeGroupEditor(false)} variant="muted" color="slate">
                Close
              </Button>
              <Button onClick={() => setShowAgeGroupEditor(false)} color="cyan">
                Done
              </Button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {flyConfirm && ReactDOM.createPortal(
        <div
          className="fixed inset-0 z-[120] flex items-center justify-center p-4"
          role="dialog"
          aria-modal="true"
          aria-labelledby="fly-standby-title"
          onClick={() => setFlyConfirm(null)}
        >
          <div className="absolute inset-0 bg-slate-950/45 backdrop-blur-sm" />
          <div
            className="relative w-full max-w-md overflow-hidden rounded-card border border-slate-200 bg-white shadow-2xl"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="flex items-start gap-3 border-b border-slate-200 px-5 py-4">
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-amber-50 text-amber-600">
                <FontAwesomeIcon icon={faTriangleExclamation} />
              </div>
              <div className="min-w-0">
                <h3 id="fly-standby-title" className="text-base font-bold text-slate-900">
                  Put {flyConfirm.app} to standby?
                </h3>
                <p className="mt-0.5 text-xs text-slate-500">
                  An event is still running
                </p>
              </div>
            </div>

            <div className="px-5 py-4">
              <p className="text-sm leading-relaxed text-slate-700">
                {flyConfirm.message}
              </p>
            </div>

            <div className="flex justify-end gap-2 border-t border-slate-200 bg-slate-50 px-5 py-3">
              <Button
                onClick={() => setFlyConfirm(null)}
                variant="muted"
                color="slate"
              >
                Cancel
              </Button>
              <Button
                onClick={() => {
                  const pending = flyConfirm;
                  setFlyConfirm(null);
                  setFlyOpsBusy("");
                  setFlyMode(pending.app, pending.mode, true);
                }}
                variant="muted"
                color="slate"
                className="!border-amber-600 !bg-amber-600 !text-white hover:!bg-amber-700"
              >
                Put to standby
              </Button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {relaySwitchConfirm && ReactDOM.createPortal(
        <div
          className="fixed inset-0 z-[120] flex items-center justify-center p-4"
          role="dialog"
          aria-modal="true"
          aria-labelledby="relay-switch-title"
          onClick={() => setRelaySwitchConfirm(null)}
        >
          <div className="absolute inset-0 bg-slate-950/45 backdrop-blur-sm" />
          <div
            className="relative w-full max-w-md overflow-hidden rounded-card border border-slate-200 bg-white shadow-2xl"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="flex items-start gap-3 border-b border-slate-200 px-5 py-4">
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-teal-50 text-teal-600">
                <FontAwesomeIcon icon={faMicrochip} />
              </div>
              <div className="min-w-0">
                <h3 id="relay-switch-title" className="text-base font-bold text-slate-900">
                  Switch active runner
                </h3>
                <p className="mt-0.5 truncate text-xs text-slate-500">
                  {relaySwitchConfirm.teamName}
                </p>
              </div>
              <button
                type="button"
                onClick={() => setRelaySwitchConfirm(null)}
                className="ml-auto flex h-8 w-8 items-center justify-center rounded-full text-slate-400 transition hover:bg-slate-100 hover:text-slate-700"
                aria-label="Close"
              >
                <FontAwesomeIcon icon={faX} />
              </button>
            </div>

            <div className="px-5 py-4">
              <div className="grid grid-cols-[1fr_auto_1fr] items-center gap-3">
                <div className="min-w-0 rounded-xl border border-slate-200 bg-slate-50 px-3 py-3">
                  <p className="text-[10px] font-semibold uppercase tracking-wide text-slate-500">Lock</p>
                  <p className="mt-1 truncate text-sm font-bold text-slate-900">BIB {relaySwitchConfirm.currentBib}</p>
                  <p className="mt-0.5 truncate text-xs text-slate-500">{relaySwitchConfirm.currentName}</p>
                </div>
                <span className="text-lg text-slate-300" aria-hidden="true">→</span>
                <div className="min-w-0 rounded-xl border border-teal-200 bg-teal-50 px-3 py-3">
                  <p className="text-[10px] font-semibold uppercase tracking-wide text-teal-700">Activate</p>
                  <p className="mt-1 truncate text-sm font-bold text-slate-900">BIB {relaySwitchConfirm.nextBib}</p>
                  <p className="mt-0.5 truncate text-xs text-slate-500">{relaySwitchConfirm.nextName}</p>
                </div>
              </div>

              <div className="mt-4 rounded-xl border border-amber-200 bg-amber-50 px-3 py-3 text-xs leading-relaxed text-amber-900">
                Confirm after the current runner's return has been recorded and the next runner has started and fully cleared the read zone. If the departure route crosses the mat, wait until that crossing is complete.
              </div>
            </div>

            <div className="flex justify-end gap-2 border-t border-slate-200 bg-slate-50 px-5 py-3">
              <Button
                onClick={() => setRelaySwitchConfirm(null)}
                variant="muted"
                color="slate"
              >
                Cancel
              </Button>
              <Button
                onClick={() => {
                  const pending = relaySwitchConfirm;
                  setRelaySwitchConfirm(null);
                  activateRelayRunner(pending);
                }}
                variant="muted"
                color="slate"
                className="!border-teal-600 !bg-teal-600 !text-white hover:!bg-teal-700"
              >
                Activate runner
              </Button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Certificate designer lives in its own overlay: the preview canvas plus
          eight setting cards do not fit inside the settings column without
          breaking its grid. */}
      {showCertDesigner && ReactDOM.createPortal(
        <div
          className="fixed inset-0 z-[100] flex items-start justify-center overflow-y-auto p-4"
          onClick={() => setShowCertDesigner(false)}
        >
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm" />
          <div
            className="relative my-8 w-full max-w-5xl rounded-card bg-white shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between border-b border-slate-200 px-5 py-3">
              <h3 className="text-base font-semibold text-slate-900">
                Certificate Design
              </h3>
              <button
                type="button"
                onClick={() => setShowCertDesigner(false)}
                className="rounded-full border border-slate-200 px-3 py-1 text-xs font-medium text-slate-600 hover:bg-slate-50"
              >
                Close
              </button>
            </div>
            <div className="px-5 pb-5">
              <CertificateDesigner
                eventName={eventName}
                background={certificateBgPreview}
                onBackgroundChange={handleCertificateBgChange}
                layout={certificateLayout}
                onLayoutChange={setCertificateLayout}
                onUploadFont={async (file) => {
                  // Shared folder, not the event's: a new event has no ID until
                  // it is saved, and a typeface is reusable across a client's
                  // events anyway. The content type is set explicitly because
                  // browsers rarely report one for font files.
                  const ext = (file.name.split(".").pop() || "ttf").toLowerCase();
                  const fontRef = ref(
                    getStorage(),
                    `event-images/cert-fonts/${Date.now()}-${file.name.replace(/[^\w.-]+/g, "_")}`,
                  );
                  try {
                    await uploadBytes(fontRef, file, { contentType: `font/${ext}` });
                    return await getDownloadURL(fontRef);
                  } catch (e) {
                    throw new Error(`Font upload failed: ${e?.message || e}`);
                  }
                }}
              />
              {/* Two different facts, and conflating them is what made the old
                  single line misleading: the draft says the work is safe from a
                  closed tab, saving the event is what makes it public. */}
              <div className="mt-3 flex flex-wrap items-center gap-x-2 gap-y-1 text-[11px]">
                {certDraftAt ? (
                  <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-50 px-2 py-0.5 font-medium text-emerald-700">
                    <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
                    Draft saved in this browser ·{" "}
                    {moment(certDraftAt).tz(userTimeZone).format("HH:mm:ss")}
                  </span>
                ) : (
                  <span className="text-slate-400">No draft yet</span>
                )}
                <span className="text-slate-500">
                  Changes go live only when you save the event.
                </span>
                {certDraftAt && (
                  <button
                    type="button"
                    onClick={() => {
                      // Discarding has to put the saved layout back on screen as
                      // well as delete the file — clearing storage alone would
                      // leave the draft's numbers in the editor and claim they
                      // were gone.
                      const saved = events.find((e) => e.eventId === selectedEventId);
                      clearCertDraft();
                      certDraftHydrated.current = null;
                      setCertificateLayout(saved?.certificateLayout || null);
                    }}
                    className="text-slate-400 underline underline-offset-2 hover:text-slate-600"
                  >
                    Discard draft
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}
    </Suspense>
  );
};

export default Admintiming;
